(function () {
  "use strict";

  // Team K9 cart page — FAST LANE v2 hydrator.
  // Checkout affordances are Liquid-rendered and never depend on this file.
  // This script hydrates offers / included value / protection into reserved
  // slots, keeps totals in sync (including after theme-side cart edits), and
  // morphs the theme cart section in place after cart mutations. Prices render
  // ONLY from the verified price map (tk9-cart-page-prices.json, built from
  // Shopify Admin variant truth).

  var initialized = false;

  function money(cents) {
    return "$" + ((Number(cents) || 0) / 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function normalize(value) {
    return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function parseJsonScript(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || "{}");
    } catch (error) {
      return {};
    }
  }

  function fetchJson(url, options) {
    return fetch(url, Object.assign({
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      }
    }, options || {})).then(function (response) {
      return response.text().then(function (text) {
        var json = text ? JSON.parse(text) : {};
        if (!response.ok) {
          var error = new Error(json && (json.description || json.message) || "Request failed");
          error.status = response.status;
          error.payload = json;
          throw error;
        }
        return json;
      });
    });
  }

  function imageUrl(config, image) {
    if (!image) return "";
    if (/^https?:\/\//i.test(image) || image.indexOf("//") === 0 || image.charAt(0) === "/") return image;
    return String(config.assetBaseUrl || "") + image;
  }

  /* ------------------------------------------------------------------ */
  /* Cart text / variant matching engine                                 */
  /* ------------------------------------------------------------------ */

  function optionText(line) {
    var parts = [];
    if (line.variant_title && line.variant_title !== "Default Title") parts.push(line.variant_title);
    if (Array.isArray(line.options_with_values)) {
      line.options_with_values.forEach(function (item) {
        if (item && item.value && item.value !== "Default Title") parts.push(item.value);
      });
    }
    return parts.join(" / ");
  }

  function lineText(line) {
    var propertyText = "";
    if (line.properties && typeof line.properties === "object") {
      propertyText = Object.keys(line.properties).map(function (key) {
        return key + " " + line.properties[key];
      }).join(" ");
    }
    return [line.title, line.product_title, line.variant_title, optionText(line), propertyText].join(" ");
  }

  function detectColor(line) {
    var text = normalize(lineText(line));
    var colors = [
      ["pink-camo", ["pink camo", "pink-camo"]],
      ["multicam", ["multicam", "multi cam"]],
      ["lavender", ["lavender", "purple"]],
      ["orange", ["orange", "blaze orange"]],
      ["green", ["green", "olive", "od green"]],
      ["brown", ["brown", "coyote"]],
      ["black", ["black", "k9 black"]],
      ["gray", ["gray", "grey"]],
      ["beige", ["beige", "tan"]]
    ];
    for (var index = 0; index < colors.length; index += 1) {
      if (colors[index][1].some(function (term) { return text.indexOf(term) !== -1; })) return colors[index][0];
    }
    return "";
  }

  function detectSize(line) {
    var text = normalize(lineText(line));
    if (/\b(extra small|extra-small|xs)\b/.test(text)) return "extra-small";
    if (/\b(small|size s)\b/.test(text)) return "small";
    if (/\b(medium|size m)\b/.test(text)) return "medium";
    // extra-large BEFORE large: "Extra-Large (Fits Bigger Trucks)" contains
    // the token "large" and was resolving XL carts to LARGE accessories
    // (owner-reported 2026-07-03).
    if (/\b(extra large|extra-large|xl)\b/.test(text)) return "extra-large";
    if (/\b(large|size l)\b/.test(text)) return "large";
    return "";
  }

  function cartProductIds(cart) {
    var ids = {};
    (cart.items || []).forEach(function (line) {
      ids[String(line.product_id)] = true;
    });
    return ids;
  }

  function lineVariantId(line) {
    return Number(line.variant_id || line.id);
  }

  function cartVariantLine(cart, variantId) {
    return (cart.items || []).find(function (line) {
      return lineVariantId(line) === Number(variantId);
    }) || null;
  }

  function cartText(cart) {
    return (cart.items || []).map(lineText).join(" ");
  }

  function containsAnyText(haystack, needles) {
    var text = normalize(haystack);
    return (needles || []).some(function (needle) {
      return text.indexOf(normalize(needle)) !== -1;
    });
  }

  function groupIsEligible(group, cart) {
    var ids = cartProductIds(cart);
    if (Array.isArray(group.suppressIfProductInCart)) {
      for (var index = 0; index < group.suppressIfProductInCart.length; index += 1) {
        if (ids[String(group.suppressIfProductInCart[index])]) return false;
      }
    }
    if (!group.triggerProductIds || !group.triggerProductIds.length) return false;
    return group.triggerProductIds.some(function (productId) {
      return Boolean(ids[String(productId)]);
    });
  }

  function triggerLineForGroup(group, cart) {
    return (cart.items || []).find(function (line) {
      return group.triggerProductIds.indexOf(Number(line.product_id)) !== -1;
    }) || (cart.items || [])[0] || null;
  }

  function offerRank(group, offer, index) {
    var ranks = {
      // Ranked by 50k-order attach data (2026-07-03, order-informed map):
      // outputs/teamk9-order-basket-analysis-20260606. Pairs with rules JSON
      // v2026-07-03-order-ranked-v4.
      "harness-setup": {
        "custom-patch": 10,
        "matched-leash": 20,
        "patch-variety": 30,
        "harness-safety-belt": 40,
        "molle-pouch": 50,
        "matched-collar": 60,
        "harness-hair-removal-gloves": 70,
        "single-style-patch": 80,
        "harness-travel-bowl": 90,
        "dog-goggles": 100,
        "first-aid-kit": 110,
        "waste-bags": 120,
        "upgrade-complete-matching-set": 900,
        "upgrade-2mbh-to-4mbh": 910
      },
      "seat-cover-setup": {
        "backseat-bed": 10,
        "dual-fans": 20, /* over belt per 21-day attributed adds, 2026-07-10 */
        "extra-safety-belt": 30,
        "cover-doggles": 40,
        "travel-bowl": 50,
        "single-seat-cover": 60,
        "cargo-cover": 70,
        "hair-removal-gloves": 80,
        "throw-blanket": 90
      },
      "pool-setup": {
        "pool-cover": 10,
        "splash-pad": 20,
        "quick-dry-towel": 30,
        "paw-drying-mat": 40
      },
      "fan-setup": {
        "fan-two-pack-upgrade": 10,
        "fan-four-pack-upgrade": 90, /* ladder infra for 3-4 kit carts; never a visible top slot (0 adds in 21d), 2026-07-10 */
        "fan-backseat-bed": 20,
        "fan-safety-belt": 30,
        "fan-hair-removal-gloves": 40,
        "fan-doggles": 50,
        "fan-travel-bowl": 60,
        "fan-throw-blanket": 70
      },
      "walk-accessory-route": {
        "second-doggles-pair": 10,
        "doggles-travel-bowl": 20,
        "first-aid-kit": 30,
        "doggles-cooling-fans": 40,
        "doggles-safety-belt": 50,
        "doggles-hair-removal-gloves": 60,
        "doggles-seat-cover": 100,
        "hero-4buckle-harness": 110,
        "custom-patch": 120,
        "matched-leash": 130,
        "patch-variety": 140,
        "single-style-patch": 150,
        "molle-pouch": 160,
        "harness-safety-belt": 170,
        "harness-travel-bowl": 180,
        "dog-goggles": 190,
        "waste-bags": 200,
        "matched-collar": 210
      },
      "glove-cleanup-setup": {
        "gloves-fan-two-pack": 10,
        "gloves-throw-blanket": 20,
        "gloves-backseat-bed": 30
      },
      "smart-best-sellers": {
        "default-seat-cover": 10,
        "default-harness": 20,
        "default-cooling-fans": 30,
        "default-travel-bowl": 40,
        "default-first-aid-kit": 50,
        "default-waste-bags": 60
      }
    };
    var groupRanks = ranks[group && group.id] || {};
    return groupRanks[offer && offer.id] || (1000 + index);
  }

  function resolveNestedVariant(mapValue, color) {
    if (!mapValue) return 0;
    if (typeof mapValue === "number" || typeof mapValue === "string") return Number(mapValue);
    if (color && mapValue[color]) return Number(mapValue[color]);
    var firstKey = Object.keys(mapValue)[0];
    return firstKey ? Number(mapValue[firstKey]) : 0;
  }

  // Upgrade (change_variant) plan: only surface an upgrade when the source
  // line is variant-matched and quantity-gated per the rules JSON, and swap
  // quantities exactly — never zero a line whose quantity the target does
  // not fully replace. No fixed-variant fallbacks: an unmapped source variant
  // drops the offer instead of guessing a size/color.
  function changeVariantPlan(offer, cart, triggerLine) {
    var sourceLine = null;
    if (Array.isArray(offer.sourceVariantIds) && offer.sourceVariantIds.length) {
      sourceLine = (cart.items || []).find(function (line) {
        return offer.sourceVariantIds.indexOf(lineVariantId(line)) !== -1;
      }) || null;
    } else {
      sourceLine = triggerLine;
    }
    if (!sourceLine) return null;

    var quantity = Number(sourceLine.quantity || 0);
    var minSource = Number(offer.minSourceQuantity || 1);
    if (quantity < minSource) return null;
    if (offer.maxSourceQuantity && quantity > Number(offer.maxSourceQuantity)) return null;

    var targetVariantId = 0;
    if (offer.targetVariantMap) {
      targetVariantId = Number(offer.targetVariantMap[String(lineVariantId(sourceLine))] || 0);
      if (!targetVariantId) return null;
    } else if (offer.targetVariantId) {
      targetVariantId = Number(offer.targetVariantId);
    }
    if (!targetVariantId) return null;

    // One target replaces `divisor` source units. Below a full divisor the
    // upgrade still stands at quantity 1 (minSourceQuantity already gated
    // eligibility — e.g. 1 single fan kit → one 2-pack); above it, floor +
    // remainder guarantees no unreplaced units are ever removed.
    var divisor = Math.max(1, Number(offer.targetQuantityDivisor || 1));
    var targetQuantity = Math.max(1, Math.floor(quantity / divisor));
    var remainder = Math.max(0, quantity - targetQuantity * divisor);

    return {
      sourceLine: sourceLine,
      targetVariantId: targetVariantId,
      targetQuantity: targetQuantity,
      remainder: remainder
    };
  }

  function resolveVariant(offer, triggerLine) {
    var color = triggerLine ? detectColor(triggerLine) : "";
    var size = triggerLine ? detectSize(triggerLine) : "";
    var variantId = Number(offer.variantId || 0);

    // Size map FIRST: the color map holds the default-size variants, so
    // checking it first sent XL carts the Large accessory (owner-reported
    // 2026-07-03) and the function paid $0 on the size mismatch.
    if (!variantId && size && offer.sizeVariantMap && offer.sizeVariantMap[size]) {
      variantId = resolveNestedVariant(offer.sizeVariantMap[size], color);
    }
    if (!variantId && color && offer.variantMap && offer.variantMap[color]) {
      variantId = Number(offer.variantMap[color]);
    }
    if (!variantId && offer.fallbackVariantId) variantId = Number(offer.fallbackVariantId);

    var image = offer.image;
    if (variantId && offer.imageVariantMap && offer.imageVariantMap[String(variantId)]) {
      image = offer.imageVariantMap[String(variantId)];
    } else if (color && offer.imageMap && offer.imageMap[color]) {
      image = offer.imageMap[color];
    }

    return {
      variantId: variantId,
      image: image,
      sourceLine: triggerLine,
      color: color,
      size: size
    };
  }

  function suppressOffer(offer, cart) {
    var ids = cartProductIds(cart);
    var quantityGuardIds = Array.isArray(offer.cartQuantityProductIds)
      ? offer.cartQuantityProductIds.map(Number)
      : [];
    if (quantityGuardIds.length) {
      var guardedQuantity = (cart.items || []).reduce(function (total, line) {
        return quantityGuardIds.indexOf(Number(line.product_id)) !== -1
          ? total + Number(line.quantity || 0)
          : total;
      }, 0);
      if (offer.minCartProductQuantity != null && guardedQuantity < Number(offer.minCartProductQuantity)) return true;
      if (offer.maxCartProductQuantity != null && guardedQuantity > Number(offer.maxCartProductQuantity)) return true;
    }
    if (offer.suppressIfProductInCart === true && offer.productId && ids[String(offer.productId)]) return true;
    if (Array.isArray(offer.suppressIfProductInCart)) {
      for (var index = 0; index < offer.suppressIfProductInCart.length; index += 1) {
        if (ids[String(offer.suppressIfProductInCart[index])]) return true;
      }
    }
    if (Array.isArray(offer.suppressIfProductIdsPresent)) {
      for (var presentIndex = 0; presentIndex < offer.suppressIfProductIdsPresent.length; presentIndex += 1) {
        if (ids[String(offer.suppressIfProductIdsPresent[presentIndex])]) return true;
      }
    }
    // Quantity-step offers intentionally add another unit of a product
    // already present. All ordinary add offers still suppress duplicates.
    if (offer.productId && offer.action !== "change_variant" && ids[String(offer.productId)] && !quantityGuardIds.length) return true;
    if (offer.suppressIfCartContainsText && containsAnyText(cartText(cart), offer.suppressIfCartContainsText)) return true;
    return false;
  }

  function chooseGroup(rules, cart) {
    var groups = (rules.offerGroups || []).slice().sort(function (left, right) {
      return Number(right.priority || 0) - Number(left.priority || 0);
    });
    var matched = groups.find(function (group) {
      return groupIsEligible(group, cart);
    });
    if (matched) return matched;

    // The rules file carries an order-ranked fallback for standalone
    // accessories and unclassified products. It previously could never run:
    // defaultFallback groups intentionally have no trigger IDs, while this
    // selector only returned trigger matches. Use it only after every
    // product-family route has declined the cart.
    return groups.find(function (group) {
      return group.defaultFallback === true;
    }) || null;
  }

  function priceEntry(prices, variantId) {
    var map = (prices && prices.prices) || {};
    return map[String(variantId)] || null;
  }

  function requiresRequiredProperties(offer) {
    return (offer.requiresProperties || []).some(function (field) {
      return Boolean(field && field.required);
    });
  }

  function resolvedOffers(group, cart, prices) {
    if (!group) return [];
    var triggerLine = triggerLineForGroup(group, cart);
    var seen = {};
    var sorted = (group.offers || []).map(function (offer, index) {
      if (offer.action === "link") return null;
      if (suppressOffer(offer, cart)) return null;

      var resolved = resolveVariant(offer, triggerLine);
      var upgradePlan = null;
      if (offer.action === "change_variant") {
        upgradePlan = changeVariantPlan(offer, cart, triggerLine);
        if (!upgradePlan) return null;
        resolved.variantId = upgradePlan.targetVariantId;
        if (offer.imageVariantMap && offer.imageVariantMap[String(upgradePlan.targetVariantId)]) {
          resolved.image = offer.imageVariantMap[String(upgradePlan.targetVariantId)];
        }
      }
      if (!resolved.variantId) return null;

      // Sold-out truth from the verified map: a variant flagged unavailable
      // never renders an Add button that can only 422.
      var entry = priceEntry(prices, resolved.variantId);
      if (entry && entry.available === false) return null;

      if (seen[String(resolved.variantId)]) return null;
      seen[String(resolved.variantId)] = true;

      // Size/color-resolved variants carry their own function prices (an XL
      // bed is $79.99 with the cover, not the Large's $69.99) — the flat
      // priceCents would overpromise and trip the self-verify.
      var variantPriceOverrides = {};
      if (offer.priceVariantMap && offer.priceVariantMap[String(resolved.variantId)]) {
        variantPriceOverrides.priceCents = Number(offer.priceVariantMap[String(resolved.variantId)]);
      }
      if (offer.standalonePriceVariantMap && offer.standalonePriceVariantMap[String(resolved.variantId)]) {
        variantPriceOverrides.standalonePriceCents = Number(offer.standalonePriceVariantMap[String(resolved.variantId)]);
      }

      return Object.assign({}, offer, resolved, variantPriceOverrides, {
        upgradePlan: upgradePlan,
        groupId: group.id,
        groupTitle: group.title,
        rank: offerRank(group, offer, index)
      });
    }).filter(Boolean).sort(function (left, right) {
      return left.rank - right.rank;
    });

    // Full eligible ladder — render() decides presentation: one featured,
    // two compact rows, and the rest in the discovery carousel. Nothing
    // eligible is cut; it just renders in a quieter portrayal.
    return sorted;
  }

  // Link-action offers (no one-tap variant) come back as view-cards that
  // deep-link to the product page via the price map's product handles.
  function linkOffersFor(group, cart, state) {
    if (!group) return [];
    return (group.offers || []).filter(function (offer) {
      return offer.action === "link" && !suppressOffer(offer, cart) && productHandle(state, offer.productId);
    }).map(function (offer) {
      return Object.assign({}, offer, {
        groupId: group.id,
        isView: true,
        variantId: Number(offer.fallbackVariantId || 0)
      });
    });
  }

  function productHandle(state, productId) {
    var map = (state.prices && state.prices.products) || {};
    var entry = map[String(productId)];
    return entry ? entry.handle : "";
  }

  // Cross-hero discovery: the store's MAIN HERO products (true cross-sell —
  // "Dog parents also build these setups"). Seed variants identify each hero;
  // titles/handles/images come from the price map's product metadata, prices
  // are live ("From $X" for multi-variant heroes, one-tap Add for
  // single-variant ones). Suppressed against the cart and shown offers.
  var HERO_SEED_VARIANTS = [
    42156188500067, // Hard Bottom Seat Cover
    41613505691747, // 4-Buckle Tactical Harness
    44107734155363, // Backseat Cooling Fans
    40324191879267, // Doggles
    39969556299875, // Foldable Dog Pool
    40095854592099  // Single-Seat Cover
  ];

  // Storewide-popularity fallback order by the shopper's active family.
  // This is intentionally NOT labeled co-purchase: family offers own the
  // evidence-based attach path; these heroes are discovery only.
  var CROSS_HERO_BY_GROUP = {
    "seat-cover-setup": [41613505691747, 39969556299875, 40324191879267, 44107734155363, 40095854592099],
    "harness-setup": [42156188500067, 44107734155363, 39969556299875, 40324191879267, 40095854592099],
    "fan-setup": [42156188500067, 41613505691747, 40324191879267, 39969556299875, 40095854592099],
    "walk-accessory-route": [41613505691747, 42156188500067, 44107734155363, 39969556299875, 40095854592099],
    "pool-setup": [42156188500067, 41613505691747, 44107734155363, 40324191879267, 40095854592099]
  };

  // Popular-product backfill for the cross module. Heroes cap at four so
  // these always get row space (owner direction 2026-07-02: bed, furniture
  // protection, and other popular products must show, not just heroes).
  // Multi-variant products deep-link ("From $X"); single-variant one-tap.
  var CROSS_FILL_VARIANTS = [
    42589237084259, // Plush Backseat Bed (2-in-1)
    44110795341923, // Throw Blanket (furniture cover)
    40095854952547, // Waterproof Cargo Area Cover
    40104811921507, // Travel Bowl
    40189461495907, // Waste Bags
    40237619904611, // First Aid Kit
    44055151509603, // MOLLE Pouch
    43325422960739, // Quick-Dry Towel
    43756025446499  // Hair Removal Gloves
  ];

  function crossHeroOffers(rules, cart, state, usedProductIds, group) {
    var prices = state.prices;
    if (!prices || !prices.prices || !prices.products) return [];
    // Asset-image fallback for products without a CDN product photo.
    var assetImageByProduct = {};
    ((rules && rules.offerGroups) || []).forEach(function (group) {
      (group.offers || []).forEach(function (offer) {
        if (offer.productId && offer.image && !assetImageByProduct[offer.productId]) {
          assetImageByProduct[offer.productId] = offer.image;
        }
      });
    });
    var byProduct = {};
    Object.keys(prices.prices).forEach(function (vid) {
      var entry = prices.prices[vid];
      (byProduct[entry.productId] = byProduct[entry.productId] || []).push({
        vid: Number(vid),
        cents: entry.cents,
        available: entry.available
      });
    });
    var inCart = cartProductIds(cart);
    var cards = [];
    var heroSeeds = CROSS_HERO_BY_GROUP[group && group.id] || HERO_SEED_VARIANTS;
    heroSeeds.forEach(function (seedVid) {
      if (cards.length >= 4) return;
      var entry = prices.prices[String(seedVid)];
      if (!entry) return;
      var pid = entry.productId;
      if (inCart[String(pid)] || usedProductIds[String(pid)]) return;
      var meta = prices.products[String(pid)];
      if (!meta || !meta.handle) return;
      var variants = (byProduct[pid] || []).filter(function (v) { return v.available !== false; });
      if (!variants.length) return;
      var minCents = Math.min.apply(null, variants.map(function (v) { return v.cents; }));
      // Heroes are entry points with size/color choices — deep-link to the
      // product page by default. One-tap Add only for truly optionless
      // products (map coverage is not proof of a single-option product).
      var oneTap = seedVid === 40324191879267; // Doggles
      cards.push({
        id: "hero-" + pid,
        productId: pid,
        title: meta.title,
        image: meta.image || assetImageByProduct[pid] || "",
        variantId: oneTap ? seedVid : 0,
        isView: !oneTap,
        heroMinCents: minCents,
        groupId: "hero-cross",
        action: "add"
      });
    });
    // Fill to eight with popular products not already on the page.
    CROSS_FILL_VARIANTS.forEach(function (seedVid) {
      if (cards.length >= 8) return;
      var entry = prices.prices[String(seedVid)];
      if (!entry || entry.available === false) return;
      var pid = entry.productId;
      if (inCart[String(pid)] || usedProductIds[String(pid)]) return;
      if (cards.some(function (card) { return card.productId === pid; })) return;
      var meta = prices.products[String(pid)];
      if (!meta) return;
      // Size/color products deep-link like heroes — never one-tap a variant
      // the shopper didn't choose.
      var fillVariants = (byProduct[pid] || []).filter(function (v) { return v.available !== false; });
      var multi = fillVariants.length > 1;
      var fillMin = fillVariants.length
        ? Math.min.apply(null, fillVariants.map(function (v) { return v.cents; }))
        : entry.cents;
      cards.push({
        id: "cross-" + pid,
        productId: pid,
        title: meta.title,
        image: meta.image || assetImageByProduct[pid] || "",
        variantId: multi ? 0 : seedVid,
        isView: multi,
        heroMinCents: multi ? fillMin : 0,
        groupId: "hero-cross",
        action: "add"
      });
    });
    return cards.slice(0, 8);
  }

  /* ------------------------------------------------------------------ */
  /* Verified pricing — the page never prints rules-JSON priceCents      */
  /* ------------------------------------------------------------------ */

  function verifiedPrice(state, variantId) {
    var entry = priceEntry(state.prices, variantId);
    if (!entry || entry.available === false) return null;
    return entry;
  }

  // Setup-bundle pricing, dictated by the live Shopify Function and verified
  // in-cart (2026-07-01): harness accessory ladder ("Team K9 Harness Bundle
  // Savings", escalating $5/$10/... marginals) and seat-cover pairings
  // ("Team K9 Seat Cover Bundle Savings", e.g. bed $89.99→$69.99). The card
  // shows the rules-JSON bundle price as the floor — the function's ladder
  // can only beat it at checkout, never undercut it. Families are enabled
  // only after their function coverage is verified against /cart.js.
  // In-cart verifications 2026-07-01: harness ("Harness Bundle Savings",
  // escalating ladder), seat ("Seat Cover Bundle Savings"), fan + doggles
  // ride route ("Active Ride Savings"), pool ("Pool Bundle Savings").
  // glove-cleanup-setup tested and NOT covered — keep it plain-priced.
  var BUNDLE_VERIFIED_GROUPS = {
    "harness-setup": true,
    "seat-cover-setup": true,
    "fan-setup": true,
    "walk-accessory-route": true,
    "pool-setup": true
  };

  function bundlePricing(state, offer) {
    if (!offer || !BUNDLE_VERIFIED_GROUPS[offer.groupId]) return null;
    if (offer.upgradePlan || offer.isView) return null;
    var live = verifiedPrice(state, offer.variantId);
    var bundle = Number(offer.priceCents || 0);
    if (!live || !bundle || bundle >= live.cents) return null;
    return {
      standaloneCents: live.cents,
      bundleCents: bundle,
      offCents: live.cents - bundle
    };
  }

  /* ------------------------------------------------------------------ */
  /* Copy — direct-response, outcome-first, checkout-truth safe          */
  /* ------------------------------------------------------------------ */

  var FAMILY_MODEL = {
    "harness-setup": { key: "harness", label: "Walk gear" },
    "walk-accessory-route": { key: "harness", label: "Car & travel" },
    "seat-cover-setup": { key: "seat", label: "Car & travel" },
    "glove-cleanup-setup": { key: "seat", label: "Car & travel" },
    "pool-setup": { key: "pool", label: "Pool time" },
    "fan-setup": { key: "fan", label: "Car & travel" }
  };

  function familyModel(group) {
    return FAMILY_MODEL[group && group.id] || { key: "cart", label: "Team K9 setup" };
  }

  // Customer-facing explanation of the Shopify Function's setup pricing —
  // shown only for families whose coverage is verified in-cart. The harness
  // ladder escalates, the others are flat pairings; the copy matches truth.
  var DEAL_NOTES = {
    "harness-setup": "Eligible setup savings apply automatically.",
    "walk-accessory-route": "Eligible setup savings apply automatically.",
    "seat-cover-setup": "Eligible setup savings apply automatically.",
    "fan-setup": "Eligible setup savings apply automatically.",
    "pool-setup": "Eligible setup savings apply automatically."
  };

  var HERO_NOUN = {
    "harness-setup": "harness",
    "walk-accessory-route": "ride setup",
    "seat-cover-setup": "seat cover",
    "fan-setup": "fans",
    "pool-setup": "pool"
  };

  function bundleContextHtml(state, offer) {
    var bundle = bundlePricing(state, offer);
    if (!bundle) return "";
    var noun = HERO_NOUN[offer.groupId] || "setup";
    return "<span class=\"tk9-ctu__deal-context\">Auto-applied with your " + escapeHtml(noun) + "</span>";
  }

  var OFFER_COPY = {
    "custom-patch": ["Make the harness recognizably theirs", "Their name on both patches. Type it once and it ships printed."],
    "matched-leash": ["Finish the handling side", "The matching bungee leash stretches to soften abrupt leash tension."],
    "patch-variety": ["Give the harness 12 messages", "Swap Service Dog, In Training, Do Not Pet, and nine more in seconds."],
    "matched-collar": ["Complete the walking set", "Same tactical build as the harness, for ID and backup control."],
    "upgrade-complete-matching-set": ["Upgrade to the complete kit", "Harness, leash, collar, and 12 patches in one matched setup."],
    "upgrade-2mbh-to-4mbh": ["Upgrade to 4 metal buckles", "Swaps the 2-buckle harness for the 4-buckle build."],
    "harness-safety-belt": ["Anchor the car ride", "Clips the harness to the seat so the drive is handled like the walk."],
    "doggles-safety-belt": ["Anchor the car ride", "Clips the harness to the seat so the drive is handled like the walk."],
    "extra-safety-belt": ["Add a second safety belt", "For the second dog or the second car, same clip-in anchor."],
    "fan-safety-belt": ["Anchor the car ride", "Clips a harness to the seat so the ride is as handled as the airflow."],
    "harness-hair-removal-gloves": ["Lift loose hair before it spreads", "Reusable gloves collect pet hair from car seats, furniture, and fabric."],
    "doggles-hair-removal-gloves": ["Lift loose hair before it spreads", "Reusable gloves collect pet hair from car seats, furniture, and fabric."],
    "hair-removal-gloves": ["Lift loose hair before it spreads", "Reusable gloves collect pet hair from car seats, furniture, and fabric."],
    "pool-hair-removal-gloves": ["Lift loose hair after the splash", "Reusable gloves collect shed hair before the ride home."],
    "fan-hair-removal-gloves": ["Lift loose hair before it spreads", "Reusable gloves collect pet hair from car seats, furniture, and fabric."],
    "molle-pouch": ["Give walk gear a place to ride", "Treats, bags, and keys clip to the harness back. 3-piece set."],
    "single-style-patch": ["Add one more patch style", "One extra hook-and-loop patch for the harness."],
    "harness-travel-bowl": ["Clip on the water stop", "Collapsible bowl that rides on the harness or leash."],
    "doggles-travel-bowl": ["Clip on the water stop", "Collapsible bowl that rides on the harness or in the door pocket."],
    "travel-bowl": ["Clip on the water stop", "Collapsible bowl that lives in the car for every ride."],
    "pool-travel-bowl": ["Keep water in reach", "Collapsible bowl for pool days and the rides after."],
    "fan-travel-bowl": ["Keep water in reach", "Collapsible bowl that lives in the car for every ride."],
    "gloves-travel-bowl": ["Keep water in reach", "Collapsible bowl that lives in the car for every ride."],
    "dog-goggles": ["Shield their eyes outdoors", "Wind, sun, and debris protection for rides and trails."],
    "cover-doggles": ["Shield their eyes on the ride", "Wind, sun, and debris protection for open windows."],
    "fan-doggles": ["Shield their eyes in the airflow", "Wind, sun, and debris protection for open windows and fans."],
    "second-doggles-pair": ["Add a second Doggles pair", "A backup pair for the second dog or the glovebox."],
    "first-aid-kit": ["Pack the trail backup", "Dog-specific first aid that lives in the car or the pack."],
    "default-first-aid-kit": ["Pack the trail backup", "Dog-specific first aid that lives in the car or the pack."],
    "waste-bags": ["Never hunt for a bag again", "Eight leak-proof rolls plus a dispenser that clips on."],
    "pool-waste-bags": ["Never hunt for a bag again", "Eight leak-proof rolls plus a dispenser that clips on."],
    "default-waste-bags": ["Never hunt for a bag again", "Eight leak-proof rolls plus a dispenser that clips on."],
    "doggles-cooling-fans": ["Aim airflow at the backseat", "Clip USB-A fans to the headrests for warm rides."],
    "dual-fans": ["Aim airflow at the backseat", "Clip USB-A fans to the headrests for warm rides."],
    "default-cooling-fans": ["Aim airflow at the backseat", "Clip USB-A fans to the headrests for warm rides."],
    "gloves-fan-two-pack": ["Add airflow to both sides", "The two-kit bundle gives each side of the backseat its own fans."],
    "fan-two-pack-upgrade": ["Add airflow to both sides", "The two-kit bundle gives each side of the backseat its own fans."],
    "fan-four-pack-upgrade": ["Build the full fan setup", "Four kits for both rows or the second car."],
    "backseat-bed": ["Complete the backseat setup", "The plush bed sits on your cover and matches its color and size."],
    "fan-backseat-bed": ["Add a plush place to ride", "The backseat bed adds a soft, quilted layer beneath your dog."],
    "gloves-backseat-bed": ["Add a plush place to ride", "The backseat bed adds a soft, quilted layer beneath your dog."],
    "single-seat-cover": ["Cover the passenger seat too", "Waterproof single-seat layer for shotgun riders."],
    "cargo-cover": ["Protect the cargo area too", "Waterproof layer for trunk and hatch runs."],
    "throw-blanket": ["Save the couch after the ride", "Water-resistant layer for furniture, beds, and travel."],
    "fan-throw-blanket": ["Catch hair and damp paws", "Water-resistant layer for the seat, couch, or crate."],
    "gloves-throw-blanket": ["Save the couch after cleanup", "Water-resistant layer for furniture, beds, and travel."],
    "pool-cover": ["Keep the pool ready", "Size-matched cover keeps leaves and paws out between sessions."],
    "quick-dry-towel": ["Make the wet ending easy", "Hand-pocket towel built for the minute right after the splash."],
    "paw-drying-mat": ["Catch the wet-paw exit", "Superabsorbent landing zone between the backyard and the house."],
    "splash-pad": ["Turn the hose into play", "Connect the hose for a fountain pad built around water-chasing fun."]
  };

  function offerCopy(offer) {
    return OFFER_COPY[offer.id] || [offer.title || "Useful extra", offer.copy || "Add it only if it fits how you use the gear."];
  }

  // Any copy containing a dollar claim is blocked unless it comes from the
  // verified price map. Applies to offer cues AND included-perk values.
  function stripDollarClaims(text) {
    var value = String(text || "");
    return /\$\s?\d/.test(value) ? "" : value;
  }

  function safeValueCue(offer) {
    return stripDollarClaims(offer.valueCue);
  }

  /* ------------------------------------------------------------------ */
  /* Telemetry                                                           */
  /* ------------------------------------------------------------------ */

  function emit(config, state, name, payload) {
    var cart = state.cart || {};
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({
      event: name,
      tk9_cart_surface_experiment: config.experimentId,
      tk9_cart_surface_variant: config.variantId,
      tk9_cart_surface: config.surface,
      tk9_cart_family: (state.currentGroup && state.currentGroup.id) || "none",
      tk9_offer_count_visible: (state.moduleCounts && state.moduleCounts.ladder) || 0,
      tk9_offer_count_extended: (state.moduleCounts && (state.moduleCounts.carousel + state.moduleCounts.cross)) || 0,
      tk9_cart_total_price: cart.total_price || 0,
      tk9_cart_item_count: cart.item_count || 0
    }, payload || {}));
  }

  function emitViewOnce(config, state) {
    var key = "tk9_ctu_view_" + (state.cart && state.cart.token || "anon");
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch (error) { /* storage unavailable — emit anyway */ }
    emit(config, state, "tk9_cart_top_upsells_view", {});
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */

  function attributionProperties(config, offer, offerModule) {
    var offerId = (offer && offer.id) || "";
    var offerGroup = (offer && offer.groupId) || "";
    return {
      "_tk9_source": "cart_top_upsells",
      "_tk9_offer_id": offerId,
      "_tk9_offer_group": offerGroup,
      /* Drawer-namespace mirrors (tk9-cart-vip-attribution-20260710): the
         order tagger and attach readouts key off _tk9_surface/_tk9_offer/
         _tk9_id/_tk9_type, so cart-page adds were order-invisible. Mirror
         both namespaces so every existing consumer sees these adds, and
         carry the module for per-surface take rates in order data. */
      "_tk9_surface": "cart",
      "_tk9_offer": offerId,
      "_tk9_id": offerId,
      "_tk9_type": offerGroup === "protection" ? "protection" : "offer",
      "_tk9_offer_module": offerModule || "ladder",
      "_tk9_cart_surface_experiment": config.experimentId,
      "_tk9_cart_surface_variant": config.variantId,
      "_tk9_cart_surface": config.surface
    };
  }

  function propertyFields(offer) {
    return (offer.requiresProperties || []).map(function (field) {
      var name = field.name || field.label || "Text";
      var isPhone = /phone/i.test(name);
      return "" +
        "<label>" +
          "<span>" + escapeHtml(field.label || name) + "</span>" +
          "<input type=\"" + (isPhone ? "tel" : "text") + "\" data-tk9-ctu-property=\"" + escapeHtml(name) + "\" data-required=\"" + (field.required ? "1" : "0") + "\" maxlength=\"" + escapeHtml(field.maxLength || 40) + "\" placeholder=\"" + escapeHtml(field.placeholder || "") + "\" autocomplete=\"" + escapeHtml(field.autocomplete || "off") + "\" autocapitalize=\"" + escapeHtml(field.autocapitalize || "characters") + "\" spellcheck=\"false\">" +
        "</label>";
    }).join("");
  }

  function collectProperties(card, offer) {
    var properties = {};
    var error = "";
    var inputs = Array.prototype.slice.call(card.querySelectorAll("[data-tk9-ctu-property]"));
    (offer.requiresProperties || []).forEach(function (field) {
      var name = field.name || field.label || "Text";
      var input = inputs.find(function (candidate) {
        return candidate.getAttribute("data-tk9-ctu-property") === name;
      });
      var value = input ? sanitizePatchText(input.value).trim() : "";
      if (field.required && !value) error = (field.label || name) + " is required.";
      if (value) properties[name] = value.toUpperCase();
    });
    return error ? { error: error, properties: null } : { error: "", properties: properties };
  }

  // Broken thumbs remove themselves — an empty image well beats a broken icon.
  // Transient CDN hiccups must not leave permanent blank wells: retry the
  // same URL once (cache-busted), then the data-fb product photo if one is
  // carried, and only then collapse the image.
  var IMG_FALLBACK = " onerror=\"if(!this.dataset.r1){this.dataset.r1='1';this.src=this.src+(this.src.indexOf('?')>-1?'&':'?')+'r1=1'}else if(this.dataset.fb&&this.src.indexOf(this.dataset.fb)===-1){this.src=this.dataset.fb}else{this.parentNode.removeChild(this)}\"";

  // Print charset gate (owner, 2026-07-03): emoji and non-ASCII glyphs
  // cannot be printed on the patch, so they never enter the input, the
  // preview, or the order properties. Printable ASCII only.
  var PATCH_TEXT_PROP = "Which text or name you want printed";
  var PATCH_PHONE_PROP = "Which phone number you want printed (optional)";
  function sanitizePatchText(value) {
    return String(value || "").replace(/[^\x20-\x7E]/g, "").replace(/\s{2,}/g, " ");
  }

  // Custom-patch live mockup — ported from the drawer (svg_v4 grammar) so the
  // preview renders the typed name/phone exactly as the order artwork will.
  function patchMockupText(value, fallback) {
    var text = sanitizePatchText(String(value || fallback || "")).trim().toUpperCase();
    return text.replace(/[&<>"']/g, function (character) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[character];
    });
  }

  function patchMockupImageData(dogName, phone) {
    var line1 = patchMockupText(dogName, "CUSTOM PATCH");
    var line2 = patchMockupText(phone, "");
    var hasPhone = Boolean(line2);
    var primarySize = line2
      ? (line1.length > 18 ? "50" : line1.length > 13 ? "60" : "76")
      : (line1.length > 16 ? "58" : line1.length > 12 ? "72" : "88");
    var phoneSize = line2.length > 24 ? "30" : line2.length > 19 ? "36" : line2.length > 14 ? "44" : "50";
    var phoneTracking = line2.length > 14 ? "1" : "2";
    var nameFit = line2
      ? (line1.length > 20 ? ' textLength="600" lengthAdjust="spacing"' : "")
      : (line1.length > 12 ? ' textLength="612" lengthAdjust="spacingAndGlyphs"' : "");
    var phoneFit = line2.length > 24 ? ' textLength="590" lengthAdjust="spacing"' : "";
    function patchMarkup(y) {
      if (hasPhone) {
        var phonePrimaryY = y + 128;
        var phoneMarkupTall = '<text x="450" y="' + (y + 216) + '" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="' + phoneSize + '" font-weight="900" fill="#ffffff" letter-spacing="' + phoneTracking + '"' + phoneFit + '>' + line2 + '</text>';
        return '<g filter="url(#shadow)"><rect x="104" y="' + y + '" width="692" height="280" rx="34" fill="#24272b" stroke="#454a50" stroke-width="10"/><rect x="142" y="' + (y + 46) + '" width="616" height="188" rx="22" fill="url(#patchBg)" stroke="#333a42" stroke-width="5"/><text x="450" y="' + phonePrimaryY + '" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="' + primarySize + '" font-weight="900" fill="#ffffff"' + nameFit + '>' + line1 + '</text>' + phoneMarkupTall + '</g>';
      }
      var primaryY = y + 122;
      return '<g filter="url(#shadow)"><rect x="85" y="' + y + '" width="730" height="190" rx="28" fill="#24272b" stroke="#454a50" stroke-width="10"/><rect x="120" y="' + (y + 32) + '" width="660" height="126" rx="18" fill="url(#patchBg)" stroke="#333a42" stroke-width="5"/><text x="450" y="' + primaryY + '" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="' + primarySize + '" font-weight="900" fill="#ffffff"' + nameFit + '>' + line1 + '</text></g>';
    }
    var svgHeight = hasPhone ? 420 : 300;
    var svgY = hasPhone ? 70 : 55;
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="900" height="' + svgHeight + '" viewBox="0 0 900 ' + svgHeight + '"><defs><linearGradient id="patchBg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#282b2f"/><stop offset=".64" stop-color="#101214"/><stop offset="1" stop-color="#25282c"/></linearGradient><filter id="shadow" x="-18%" y="-24%" width="136%" height="160%"><feDropShadow dx="0" dy="12" stdDeviation="14" flood-color="#101828" flood-opacity=".24"/></filter></defs><rect width="900" height="' + svgHeight + '" rx="30" fill="#f7f8fa"/>' + patchMarkup(svgY) + '</svg>';
    return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
  }

  function isPatchOffer(offer) {
    return offer && (offer.id === "custom-patch" || String(offer.id).indexOf("custom-patch") !== -1);
  }

  function patchFieldValues(card) {
    var inputs = card.querySelectorAll("[data-tk9-ctu-property]");
    var name = "", phone = "";
    inputs.forEach(function (input) {
      var key = String(input.getAttribute("data-tk9-ctu-property") || "");
      if (/phone/i.test(key)) phone = input.value;
      else name = input.value;
    });
    return { name: name, phone: phone };
  }

  function updatePatchPreview(card) {
    var img = card.querySelector("[data-tk9-ctu-patch-preview]");
    if (!img) return;
    var values = patchFieldValues(card);
    img.src = patchMockupImageData(values.name, values.phone);
  }

  function mediaHtml(config, offer, state) {
    if (isPatchOffer(offer)) {
      return "<div class=\"tk9-ctu__media tk9-ctu__media--patch\"><img data-tk9-ctu-patch-preview src=\"" + patchMockupImageData("", "") + "\" alt=\"\" width=\"200\" height=\"200\" decoding=\"async\"></div>";
    }
    // The product's CDN photo backstops the offer's asset image: as the
    // primary when the offer has none, as an onerror retry otherwise —
    // a single transient failure must not leave a blank well.
    var products = state && state.prices && state.prices.products;
    var productImage = (products && offer.productId && products[String(offer.productId)] || {}).image || "";
    var image = imageUrl(config, offer.image) || productImage;
    if (!image) return "<div class=\"tk9-ctu__media\" aria-hidden=\"true\"></div>";
    var fallback = (productImage && productImage !== image
      ? " data-fb=\"" + escapeHtml(productImage) + "\""
      : "") + IMG_FALLBACK;
    return "<div class=\"tk9-ctu__media\"><img src=\"" + escapeHtml(image) + "\" alt=\"\" width=\"200\" height=\"200\" loading=\"lazy\" decoding=\"async\"" + fallback + "></div>";
  }

  function priceRowHtml(state, offer) {
    var bundle = bundlePricing(state, offer);
    if (bundle) {
      return "<div class=\"tk9-velocity-offer__price\">" +
        "<b>" + escapeHtml(money(bundle.bundleCents)) + "</b>" +
        "<s>" + escapeHtml(money(bundle.standaloneCents)) + "</s>" +
        "<span class=\"tk9-save-pill tk9-save-pill--card\">" + escapeHtml(money(bundle.offCents)) + " OFF</span>" +
      "</div>";
    }
    var live = verifiedPrice(state, offer.variantId);
    if (!live) return "";
    if (offer.upgradePlan) {
      // Upgrades sell the bundle math, never a bare price: pack price vs the
      // verified cost of buying the same units singly (source line's live
      // price × units replaced), or the map's true anchor — whichever is a
      // bigger VERIFIED number. No claim if neither beats the pack price.
      var plan = offer.upgradePlan;
      var payCents = live.cents * plan.targetQuantity;
      var perUnits = Math.max(1, Number(offer.targetQuantityDivisor || 1)) * plan.targetQuantity;
      var sourceLive = plan.sourceLine ? verifiedPrice(state, lineVariantId(plan.sourceLine)) : null;
      var upgradeAnchor = Math.max(
        sourceLive ? sourceLive.cents * perUnits : 0,
        Number(live.anchorCents || 0) * plan.targetQuantity
      );
      if (upgradeAnchor > payCents) {
        return "<div class=\"tk9-velocity-offer__price\">" +
          "<b>" + escapeHtml(money(payCents)) + "</b>" +
          "<s>" + escapeHtml(money(upgradeAnchor)) + "</s>" +
          "<span class=\"tk9-save-pill tk9-save-pill--card\">" + escapeHtml(money(upgradeAnchor - payCents)) + " OFF</span>" +
        "</div>";
      }
      return "<div class=\"tk9-velocity-offer__price\"><b>" + escapeHtml(money(payCents)) + "</b></div>";
    }
    var cents = live.cents;
    var compare = live.compareAtCents > live.cents
      ? "<s>" + escapeHtml(money(live.compareAtCents)) + "</s>"
      : "";
    return "<div class=\"tk9-velocity-offer__price\"><b>" + escapeHtml(money(cents)) + "</b>" + compare + "</div>";
  }

  function addLabel(state, offer) {
    // Owner direction 2026-07-02: the price row above the button owns the
    // number; the button just says what it does.
    if (offer.action === "change_variant") return offer.buttonLabel || "Upgrade setup";
    return "Add to cart";
  }

  function renderPrimaryOffer(config, state, offer) {
    var copy = offerCopy(offer);
    var includesHtml = "";
    if (Array.isArray(offer.displayIncludes) && offer.displayIncludes.length) {
      // Drawer convention: show the FULL include list (all 12 patches, not a
      // teaser). maxDisplayIncludes caps it when the data says so.
      var includeLimit = Math.max(1, Number(offer.maxDisplayIncludes || 12));
      includesHtml = "<ul class=\"tk9-ctu__offer-includes\">" + offer.displayIncludes.slice(0, includeLimit).map(function (line) {
        // Same money-promise containment as cues/perks: an unverified dollar
        // claim in rules-JSON free text never prints.
        return stripDollarClaims(line) ? "<li>" + escapeHtml(stripDollarClaims(line)) + "</li>" : "";
      }).join("") + "</ul>";
    }
    return "" +
      "<article class=\"tk9-velocity-offer--primary\" data-tk9-ctu-offer=\"" + escapeHtml(offer.id) + "\" data-tk9-offer-group=\"" + escapeHtml(offer.groupId || "") + "\">" +
        mediaHtml(config, offer, state) +
        "<div class=\"tk9-velocity-offer__body\">" +
          "<span class=\"tk9-velocity-offer__tag\">" + (offer.upgradePlan ? "Bundle &amp; save" : "Recommended for you") + "</span>" +
          "<h3>" + escapeHtml(copy[0]) + "</h3>" +
          "<p>" + escapeHtml(copy[1]) + "</p>" +
          includesHtml +
          "<div class=\"tk9-ctu__properties\">" + propertyFields(offer) + "</div>" +
          "<p class=\"tk9-ctu__error\" data-tk9-ctu-error role=\"alert\" hidden></p>" +
        "</div>" +
        "<div class=\"tk9-velocity-offer__action\">" +
          priceRowHtml(state, offer) +
          "<button class=\"tk9-ctu__add\" type=\"button\" data-tk9-ctu-add=\"" + escapeHtml(offer.id) + "\">" + escapeHtml(addLabel(state, offer)) + "</button>" +
        "</div>" +
      "</article>";
  }

  function renderCompactOffer(config, state, offer) {
    var copy = offerCopy(offer);
    var cue = safeValueCue(offer);
    return "" +
      "<article class=\"tk9-velocity-offer--compact\" data-tk9-ctu-offer=\"" + escapeHtml(offer.id) + "\">" +
        mediaHtml(config, offer, state) +
        "<div>" +
          "<h3>" + escapeHtml(copy[0]) + "</h3>" +
          "<p>" + escapeHtml(cue || copy[1]) + "</p>" +
          priceRowHtml(state, offer) +
          "<p class=\"tk9-ctu__error\" data-tk9-ctu-error role=\"alert\" hidden></p>" +
        "</div>" +
        "<button class=\"tk9-ctu__add tk9-ctu__add--ghost\" type=\"button\" data-tk9-ctu-add=\"" + escapeHtml(offer.id) + "\">" + escapeHtml(addLabel(state, offer)) + "</button>" +
      "</article>";
  }

  // Module slots live inside the theme cart section's items column (the
  // gated host in main-cart.liquid), which section morphs replace — always
  // re-query, never cache.
  function moduleHost(root) {
    return document.querySelector("[data-tk9-ctu-modules-host]") || root;
  }

  // Carousel tile — the quiet discovery portrayal. Product-name-led (FBT
  // grammar), verified price only, Add for one-tap variants, View options
  // deep link when the product needs a size/color choice.
  function renderTile(config, state, offer, moduleName) {
    var media = mediaHtml(config, offer, state);
    var price = offer.variantId ? priceRowHtml(state, offer) : "";
    if (!price && offer.heroMinCents) {
      price = "<div class=\"tk9-velocity-offer__price\"><b>From " + escapeHtml(money(offer.heroMinCents)) + "</b></div>";
    }
    var action;
    if (offer.isView) {
      var handle = productHandle(state, offer.productId);
      // Personalization offers keep the deep link — the product page owns
      // the input flow. Plain multi-variant products pick options in-tile.
      var pickable = handle && offer.productId && !(offer.requiresProperties || []).length;
      if (pickable) {
        action = "<button class=\"tk9-ctu__add tk9-ctu__add--ghost tk9-ctu__add--tile\" type=\"button\" data-tk9-ctu-choose=\"" + escapeHtml(offer.id) + "\" data-tk9-ctu-handle=\"" + escapeHtml(handle) + "\" data-tk9-ctu-module=\"" + escapeHtml(moduleName) + "\">Choose options</button>" +
          "<div class=\"tk9-ctu__picker\" data-tk9-ctu-picker hidden></div>";
      } else {
        action = "<a class=\"tk9-ctu__add tk9-ctu__add--ghost tk9-ctu__add--tile\" href=\"/products/" + escapeHtml(handle) + "\" data-tk9-ctu-view=\"" + escapeHtml(offer.id) + "\">View options</a>";
      }
    } else {
      action = "<button class=\"tk9-ctu__add tk9-ctu__add--tile\" type=\"button\" data-tk9-ctu-add=\"" + escapeHtml(offer.id) + "\" data-tk9-ctu-module=\"" + escapeHtml(moduleName) + "\">" + escapeHtml(addLabel(state, offer)) + "</button>";
    }
    return "" +
      "<article class=\"tk9-velocity-offer--tile\" data-tk9-ctu-offer=\"" + escapeHtml(offer.id) + "\">" +
        media +
        "<h3>" + escapeHtml(offer.title || offerCopy(offer)[0]) + "</h3>" +
        price +
        "<p class=\"tk9-ctu__error\" data-tk9-ctu-error role=\"alert\" hidden></p>" +
        action +
      "</article>";
  }

  function renderCarousel(host, slotSelector, wrapSelector, config, state, offers, moduleName, noteText, headingText) {
    var wrap = host.querySelector(wrapSelector);
    var list = host.querySelector(slotSelector);
    if (!wrap || !list) return;
    if (!offers.length) {
      wrap.hidden = true;
      list.innerHTML = "";
      return;
    }
    var heading = wrap.querySelector(".tk9-ctu__section-intro");
    if (heading && headingText) heading.textContent = headingText;
    list.innerHTML = offers.map(function (offer) {
      return renderTile(config, state, offer, moduleName);
    }).join("");
    var note = wrap.querySelector(".tk9-ctu__deal-note");
    if (noteText) {
      if (!note) {
        note = document.createElement("p");
        note.className = "tk9-ctu__deal-note";
        list.parentNode.insertBefore(note, list);
      }
      note.textContent = noteText;
    } else if (note) {
      note.remove();
    }
    wrap.hidden = false;
  }

  // Included value renders ONLY for groups that declare their own perk key
  // (rules JSON sets includedPerkKey on harness-setup and seat-cover-setup
  // alone). Deriving perks from the family tint key would print "Included"
  // claims for carts that do not contain the qualifying product.
  function renderIncluded(root, config, rules, perkKey, group, cart) {
    var host = moduleHost(root);
    var wrap = host.querySelector("[data-tk9-ctu-included]");
    var list = host.querySelector("[data-tk9-ctu-included-list]");
    var label = host.querySelector("[data-tk9-ctu-included-label]");
    if (!wrap || !list) return;
    var perks = perkKey ? (rules && rules.includedPerks || {})[perkKey] : null;
    if (!perks || !Array.isArray(perks.items) || !perks.items.length) {
      wrap.hidden = true;
      list.innerHTML = "";
      return;
    }
    if (label) label.textContent = stripDollarClaims(perks.title) || "Included with your gear";
    var summary = stripDollarClaims(perks.summary);
    if (perkKey === "seat" && group && cart) {
      var triggerIds = (group.triggerProductIds || []).map(String);
      var qualifyingQty = (cart.items || []).reduce(function (total, item) {
        return triggerIds.indexOf(String(item.product_id)) >= 0
          ? total + (Number(item.quantity) || 0)
          : total;
      }, 0);
      if (qualifyingQty >= 2) {
        summary = "Your additional cover is already in the cart. Each qualifying cover includes its safety belt.";
      }
    }
    var summaryNode = wrap.querySelector(".tk9-ctu__included-summary");
    if (summary) {
      if (!summaryNode) {
        summaryNode = document.createElement("p");
        summaryNode.className = "tk9-ctu__included-summary";
        wrap.insertBefore(summaryNode, list);
      }
      summaryNode.textContent = summary;
    } else if (summaryNode) {
      summaryNode.remove();
    }
    list.innerHTML = perks.items.slice(0, 4).map(function (item) {
      var image = imageUrl(config, item && item.image);
      var media = image
        ? "<span class=\"tk9-ctu__included-media\"><img src=\"" + escapeHtml(image) + "\" alt=\"\" width=\"84\" height=\"84\" loading=\"lazy\" decoding=\"async\"" + IMG_FALLBACK + "></span>"
        : "";
      var safeValue = stripDollarClaims(item && item.value);
      var value = safeValue ? "<small>" + escapeHtml(safeValue) + "</small>" : "";
      return "<li>" + media +
        "<span class=\"tk9-ctu__included-text\">" + escapeHtml(item && item.label || "") + value + "</span>" +
        "<span class=\"tk9-ctu__free-pill\">" + escapeHtml(perks.claim || "Included") + "</span></li>";
    }).join("");
    wrap.hidden = false;
  }

  function renderProtection(root, config, state) {
    var host = moduleHost(root);
    var wrap = host.querySelector("[data-tk9-ctu-protect]");
    var list = host.querySelector("[data-tk9-ctu-protect-list]");
    if (!wrap || !list) return;
    var items = (state.rules && state.rules.protection) || [];
    var html = items.map(function (item, index) {
      var live = verifiedPrice(state, item.variantId);
      if (!live) return "";
      var inCart = Boolean(cartVariantLine(state.cart, item.variantId));
      var titleId = "tk9-ctu-protect-title-" + index;
      var image = item.image
        ? "<div class=\"tk9-ctu__media\"><img src=\"" + escapeHtml(item.image) + "\" alt=\"\" width=\"88\" height=\"88\" loading=\"lazy\" decoding=\"async\"" + IMG_FALLBACK + "></div>"
        : "<div class=\"tk9-ctu__media\" aria-hidden=\"true\"></div>";
      return "" +
        "<div class=\"tk9-ctu__protect-row\">" +
          image +
          "<div><h3 id=\"" + titleId + "\">" + escapeHtml(item.title) + "</h3>" +
          "<p>" + escapeHtml(item.copy || "") + "</p></div>" +
          "<span class=\"tk9-ctu__protect-price\">" + escapeHtml(money(live.cents)) + "</span>" +
          "<button class=\"tk9-ctu__switch\" type=\"button\" role=\"switch\" aria-checked=\"" + (inCart ? "true" : "false") + "\" aria-labelledby=\"" + titleId + "\" data-tk9-ctu-protect-toggle=\"" + escapeHtml(item.id) + "\"></button>" +
        "</div>";
    }).join("");
    if (!html) {
      wrap.hidden = true;
      list.innerHTML = "";
      return;
    }
    list.innerHTML = html;
    wrap.hidden = false;
  }

  function updateTotals(root, state) {
    var cart = state.cart || {};
    document.querySelectorAll("[data-tk9-ctu-total]").forEach(function (node) {
      node.textContent = money(cart.total_price);
    });
    var compare = root.querySelector("[data-tk9-ctu-total-compare]");
    if (compare) {
      if (Number(cart.original_total_price) > Number(cart.total_price)) {
        compare.textContent = money(cart.original_total_price);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }
    var saved = root.querySelector("[data-tk9-ctu-saved]");
    if (saved) {
      if (Number(cart.total_discount) > 0) {
        saved.innerHTML = "<i aria-hidden=\"true\">$</i>" + escapeHtml(money(cart.total_discount)) + " SAVED";
        saved.hidden = false;
      } else {
        saved.hidden = true;
      }
    }
    var shipping = root.querySelector("[data-tk9-ctu-shipping]");
    if (shipping) {
      var threshold = 9900;
      if (Number(cart.total_price) >= threshold) {
        shipping.classList.add("is-good");
        shipping.innerHTML = "<i aria-hidden=\"true\"></i>FREE shipping";
      } else {
        shipping.classList.remove("is-good");
        shipping.textContent = money(threshold - Number(cart.total_price)) + " to FREE shipping";
      }
    }
  }

  // One checkout voice: retokenize the theme rail button's label with the
  // live total. Styling comes from the gated CSS in main-cart.liquid. Skipped
  // when the button carries child markup (spinners etc.) we must not destroy.
  function updateThemeCheckoutButton(state) {
    var button = document.getElementById("checkout");
    if (!button || !state.cart) return;
    if (button.children.length) return;
    // No price on the rail button — the Estimated total row directly above
    // it owns the number (price shown once in the rail).
    button.textContent = "Secure checkout";
  }

  // Percentage of an order-level code, taken from the code itself (never
  // computed per line, so the claim is exactly what checkout honors).
  function cartPercentageOff(state) {
    var apps = (state.cart && state.cart.cart_level_discount_applications) || [];
    for (var index = 0; index < apps.length; index += 1) {
      if (String(apps[index].value_type) === "percentage") {
        var pct = Math.round(Number(apps[index].value));
        if (pct >= 1 && pct <= 99) return pct;
      }
    }
    return 0;
  }

  // Per-line TRUE savings: the strikethrough anchors to the variant's real
  // "was" value (compare-at, kit component sums, or +FREE-inclusion value
  // from the price map) and the −N% is the TOTAL discount vs that anchor —
  // bundle pricing, line discounts, and order codes combined. Every input
  // comes from /cart.js truth or the build-verified anchor map.
  function lineUnitAnchor(state, item) {
    var entry = priceEntry(state.prices, item.variant_id || item.id);
    var anchor = Math.max(Number(entry && entry.anchorCents || 0), Number(item.price) || 0);
    return anchor;
  }

  // Rows resolve to cart items by the variant id in the row's product link —
  // NEVER by bare index. An app-injected row (free gift, disclosure) would
  // shift a positional zip and print another line's price claim. Positional
  // fallback only when the counts match exactly; otherwise skip (no claim
  // beats a wrong claim).
  function rowVariantId(row) {
    var link = row.querySelector("a[href*='variant=']");
    var match = link && String(link.getAttribute("href")).match(/[?&]variant=(\d+)/);
    return match ? Number(match[1]) : 0;
  }

  function decorateCartLines(state) {
    var rows = document.querySelectorAll(".cart-items__table tbody tr");
    var items = (state.cart && state.cart.items) || [];
    var pct = cartPercentageOff(state);
    var claimed = {};
    rows.forEach(function (row, index) {
      var existing = row.querySelector(".tk9-line-deal");
      if (existing) existing.remove();
      // Undo a previous pass's hides first: if this row skips decoration now
      // (discount gone), the theme's native compare-at must return.
      row.querySelectorAll("[data-tk9-native-hidden]").forEach(function (node) {
        node.style.removeProperty("display");
        node.removeAttribute("data-tk9-native-hidden");
      });
      var vid = rowVariantId(row);
      var item = null;
      if (vid) {
        for (var i = 0; i < items.length; i += 1) {
          if (!claimed[i] && lineVariantId(items[i]) === vid) {
            claimed[i] = true;
            item = items[i];
            break;
          }
        }
      } else if (rows.length === items.length) {
        item = items[index];
      }
      if (!item) return;
      var anchorEl = row.querySelector(".cart-items__details");
      if (!anchorEl) return;
      var unitAnchor = lineUnitAnchor(state, item);
      var effectiveUnit = Math.round(Number(item.final_price) * (1 - pct / 100));
      if (unitAnchor <= effectiveUnit) return;
      var totalPct = Math.round((1 - effectiveUnit / unitAnchor) * 100);
      if (totalPct < 1) return;
      var deal = document.createElement("div");
      deal.className = "tk9-line-deal";
      deal.innerHTML = "<s>" + escapeHtml(money(unitAnchor)) + "</s>" +
        "<span class=\"tk9-pct-pill\">−" + totalPct + "%</span>";
      anchorEl.appendChild(deal);
      // ONE price story per line: our row (true anchor + total −N%) replaces
      // the theme's compare-at strike and $-off badge — showing both reads as
      // a double strikethrough with two competing claims. Only hidden when we
      // actually injected; undecorated lines keep the theme's native pair.
      // setProperty(..., "important"): a theme rule forces the badge visible
      // with display:...!important, so a plain inline display loses.
      row.querySelectorAll("s, .cart-savings-badge").forEach(function (node) {
        if (node.closest(".tk9-line-deal") || node.classList.contains("tk9-vip-line-anchor")) return;
        node.style.setProperty("display", "none", "important");
        node.setAttribute("data-tk9-native-hidden", "1");
      });
      row.querySelectorAll("span, div, dd").forEach(function (node) {
        if (node.closest(".tk9-line-deal") || node.childElementCount > 1) return;
        if (/line-through/.test(getComputedStyle(node).textDecorationLine || "")) {
          node.style.setProperty("display", "none", "important");
          node.setAttribute("data-tk9-native-hidden", "1");
        }
      });
    });
  }

  // Custom patch cart lines: the shopper's typed entry becomes the visual.
  // The line thumbnail swaps to the SAME svg mockup the offer card previews,
  // and the raw property rows collapse into a patch-styled chip (owner
  // direction 2026-07-02).
  function formatPatchPhoneDisplay(value) {
    var raw = String(value || "").trim();
    var digits = raw.replace(/\D/g, "");
    if (digits.length === 11 && digits.charAt(0) === "1") digits = digits.slice(1);
    if (digits.length !== 10) return raw;
    return "(" + digits.slice(0, 3) + ") " + digits.slice(3, 6) + "-" + digits.slice(6);
  }

  function decoratePatchLines(state) {
    var rows = document.querySelectorAll(".cart-items__table tbody tr");
    var items = (state.cart && state.cart.items) || [];
    rows.forEach(function (row) {
      var vid = rowVariantId(row);
      if (!vid) return;
      var item = null;
      for (var i = 0; i < items.length; i += 1) {
        var candidate = items[i];
        var props = candidate.properties || {};
        if (lineVariantId(candidate) === vid && (props._tk9_patch_mockup_line_1 || props._tk9_patch_mockup_line_2)) {
          item = candidate;
          break;
        }
      }
      if (!item) return;
      var props = item.properties || {};
      var line1 = sanitizePatchText(props._tk9_patch_mockup_line_1).trim().toUpperCase();
      var line2 = formatPatchPhoneDisplay(sanitizePatchText(props._tk9_patch_mockup_line_2).trim().toUpperCase());
      var stamp = line1 + "|" + line2;

      var img = row.querySelector("img");
      if (img && img.getAttribute("data-tk9-patch-render") !== stamp) {
        img.setAttribute("data-tk9-patch-render", stamp);
        img.removeAttribute("srcset");
        img.src = patchMockupImageData(line1, line2);
        // The theme forces object-fit: cover with !important on line media —
        // match its weight or the mockup renders zoomed/cropped (owner-
        // reported on mobile, 2026-07-03).
        img.style.setProperty("object-fit", "contain", "important");
        img.style.setProperty("background", "#ffffff", "important");
      }

      // The theme renders line properties in .cart-items__properties — the
      // chip replaces them wholesale on patch lines (structural hide; the
      // exact-text pass below stays as a fallback for other markups, since
      // formatted values like "(508) 509-2697" can drift from stored text).
      row.querySelectorAll(".cart-items__properties").forEach(function (node) {
        node.style.setProperty("display", "none", "important");
      });
      // Hide the theme's raw property rows (exact text match on key/value
      // leaf nodes only — nothing else in the row shares these strings).
      var rawTexts = [];
      Object.keys(props).forEach(function (key) {
        if (key.charAt(0) === "_") return;
        rawTexts.push(String(key).trim());
        rawTexts.push(String(props[key]).trim());
        rawTexts.push((String(key) + ": " + String(props[key])).trim());
      });
      if (rawTexts.length) {
        row.querySelectorAll("span, p, dd, dt, div, li, small").forEach(function (node) {
          if (node.childElementCount > 0) return;
          if (node.closest(".tk9-line-patch") || node.closest(".tk9-line-deal")) return;
          var text = String(node.textContent || "").trim();
          if (text && rawTexts.indexOf(text) !== -1) node.style.setProperty("display", "none", "important");
        });
      }

      var existing = row.querySelector(".tk9-line-patch");
      if (existing && existing.getAttribute("data-tk9-patch-render") === stamp) return;
      if (existing) existing.remove();
      var host = row.querySelector(".cart-items__details") || row;
      var chip = document.createElement("div");
      chip.className = "tk9-line-patch";
      chip.setAttribute("data-tk9-patch-render", stamp);
      // The thumbnail mockup shows the artwork once; the entry itself reads
      // as plain text beside the print note (owner direction 2026-07-02:
      // show the mockup AND the typed text, never two patch images).
      var entryText = line1 + (line2 ? " \u00b7 " + line2 : "");
      chip.innerHTML = "<span class=\"tk9-line-patch__entry\">" + escapeHtml(entryText) + "</span>" +
        "<span class=\"tk9-line-patch__note\">Printed on both patches</span>";
      host.appendChild(chip);
    });
  }

  // A patch line with no printed text cannot be fulfilled (owner,
  // 2026-07-03): native PDP adds skip the input flow, so the cart is the
  // gate. Render a fix-it card on the line and hold checkout until typed.
  function patchTextMissing(item) {
    if (!/custom harness patch/i.test(String(item.product_title || ""))) return false;
    var props = item.properties || {};
    return !sanitizePatchText(props[PATCH_TEXT_PROP]).trim() && !sanitizePatchText(props._tk9_patch_mockup_line_1).trim();
  }

  function decorateTextlessPatchLines(state) {
    var items = (state.cart && state.cart.items) || [];
    state.patchTextMissing = items.some(patchTextMissing);
    var rows = document.querySelectorAll(".cart-items__table tbody tr");
    rows.forEach(function (row) {
      var vid = rowVariantId(row);
      if (!vid) return;
      var item = null;
      for (var i = 0; i < items.length; i += 1) {
        if (lineVariantId(items[i]) === vid && patchTextMissing(items[i])) {
          item = items[i];
          break;
        }
      }
      var existing = row.querySelector("[data-tk9-patchfix]");
      if (!item) {
        if (existing) existing.remove();
        return;
      }
      if (existing) return;
      var host = row.querySelector(".cart-items__details") || row.querySelector("td:last-child") || row;
      var box = document.createElement("div");
      box.className = "tk9-line-patchfix";
      box.setAttribute("data-tk9-patchfix", "");
      box.setAttribute("data-tk9-patchfix-key", String(item.key || ""));
      box.innerHTML =
        "<p class=\"tk9-line-patchfix__msg\">This patch needs the text you want printed. Type it below, or remove the patch.</p>" +
        "<div class=\"tk9-line-patchfix__row\">" +
          "<input type=\"text\" maxlength=\"22\" placeholder=\"Name or message\" autocapitalize=\"characters\" spellcheck=\"false\">" +
          "<button type=\"button\" data-tk9-patchfix-save>Save patch text</button>" +
        "</div>" +
        "<p class=\"tk9-line-patchfix__error\" role=\"alert\" hidden></p>";
      host.appendChild(box);
    });
  }

  // Rail Estimated total: strike the TRUE anchor total (bundle value + all
  // discounts included) and show the total −N% against it.
  function decorateRailTotal(state, isRetry) {
    var container = document.querySelector(".cart__total-container");
    if (!container || !state.cart) return;
    container.querySelectorAll(".tk9-pct-pill, .tk9-rail-anchor").forEach(function (el) { el.remove(); });
    // Undo hides from a previous pass before deciding anything — if this pass
    // skips decoration (code removed in another tab), the theme's own struck
    // price must come back rather than stay display:none forever.
    container.querySelectorAll("[data-tk9-native-hidden]").forEach(function (el) {
      el.style.removeProperty("display");
      el.removeAttribute("data-tk9-native-hidden");
    });
    var items = state.cart.items || [];
    var anchorTotal = items.reduce(function (acc, item) {
      return acc + lineUnitAnchor(state, item) * Number(item.quantity || 1);
    }, 0);
    var effectiveTotal = Number(state.cart.total_price) || 0;
    if (!anchorTotal || anchorTotal <= effectiveTotal) return;
    var totalPct = Math.round((1 - effectiveTotal / anchorTotal) * 100);
    if (totalPct < 1) return;
    // The theme's struck total is span.cart__total-original (renders with a
    // code active) — reuse it as the anchor node; otherwise insert our own.
    var struck = container.querySelector(".cart__total-original");
    if (!struck || !/\d/.test(struck.textContent)) struck = container.querySelector("s, del");
    if (struck) {
      struck.textContent = money(anchorTotal) + " USD";
      struck.style.display = "";
    } else {
      var value = container.querySelector(".cart__total-value") || container;
      struck = document.createElement("s");
      struck.className = "tk9-rail-anchor";
      struck.textContent = money(anchorTotal) + " USD";
      value.insertBefore(struck, value.firstChild);
    }
    var pill = document.createElement("span");
    pill.className = "tk9-pct-pill";
    pill.textContent = "−" + totalPct + "%";
    struck.insertAdjacentElement("afterend", pill);

    // ONE strike only: hide any other line-through price in the container.
    container.querySelectorAll("s, del, span").forEach(function (el) {
      if (el === struck || struck.contains(el) || el.contains(struck)) return;
      if (!/\$\s?[\d,.]+/.test(el.textContent)) return;
      var deco = getComputedStyle(el).textDecorationLine || getComputedStyle(el).textDecoration || "";
      if (/line-through/.test(deco)) {
        el.style.display = "none";
        el.setAttribute("data-tk9-native-hidden", "1");
      }
    });

    // The theme's own struck subtotal hydrates late (with installments) —
    // rerun ONCE per external call. The retry itself never reschedules
    // (a reset-inside-the-timeout flag here previously self-rearmed into an
    // infinite 2.5s redecorate loop).
    if (!isRetry) {
      if (decorateRailTotal.timer) clearTimeout(decorateRailTotal.timer);
      decorateRailTotal.timer = setTimeout(function () {
        decorateRailTotal.timer = null;
        decorateRailTotal(state, true);
      }, 2500);
    }
  }

  // Replace the plain-text trust pill in the rail with an icon row. The pill
  // mounts late (alongside the wallet components), so this runs on render
  // AND on delayed retries from init.
  // The theme's trust pill is a base.css ::after on .cart__ctas (pure CSS
  // content — no DOM node), which glues it under the wallets and sandwiches
  // dead space around the Discount accordion. The gated cart-page style
  // suppresses that pseudo; this renders a real icon row parked at the very
  // bottom of the summary, below subtotal + discount rows (owner direction
  // 2026-07-02). Runs on render AND on delayed retries from init.
  // The slot's styles travel WITH this script (not only in the section's
  // gated <style>): cart morphs can execute fresh JS inside a page still
  // holding older section CSS, and the row rendered unstyled/full-width in
  // that mix (seen live 2026-07-02). Scoped to .tk9-trust-slot so the
  // drawer's own trust row is untouched.
  function ensureTrustStyles() {
    if (document.getElementById("tk9-trust-slot-style")) return;
    var style = document.createElement("style");
    style.id = "tk9-trust-slot-style";
    style.textContent =
      ".tk9-trust-slot{order:5;margin:10px 0 0;padding:6px 10px;border:1px solid rgba(47,133,90,.24);border-radius:8px;background:#f2f8f5}" +
      ".tk9-trust-slot .tk9-trust-row{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;padding:8px 2px 2px}" +
      ".tk9-trust-slot .tk9-trust-row span{display:grid;justify-items:center;gap:5px;min-width:0;color:#0d713f;font-size:10.5px;font-weight:700;line-height:1.25;text-align:center;white-space:normal;overflow-wrap:break-word}" +
      ".tk9-trust-slot .tk9-trust-row svg{width:20px;height:20px}";
    document.head.appendChild(style);
  }

  function upgradeTrustRow() {
    var inner = document.querySelector(".cart__summary-inner");
    if (!inner) return;
    // The VIP cart has its own gold 60-day guarantee card and compact SSL
    // assurance. Do not duplicate it with the generic green three-icon row.
    if (document.querySelector(".cart-page.tk9-vip")) {
      var vipSlot = inner.querySelector(":scope > .tk9-trust-slot");
      if (vipSlot) vipSlot.remove();
      return;
    }
    ensureTrustStyles();
    var slot = inner.querySelector(":scope > .tk9-trust-slot");
    if (slot) {
      if (inner.lastElementChild !== slot) inner.appendChild(slot);
      return;
    }
    slot = document.createElement("div");
    slot.className = "tk9-trust-slot";
    slot.innerHTML = "<div class=\"tk9-trust-row\">" +
      "<span><svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><path d=\"M1 3h15v13H1zM16 8h4l3 3v5h-7V8z\"/><circle cx=\"5.5\" cy=\"18.5\" r=\"2.5\"/><circle cx=\"18.5\" cy=\"18.5\" r=\"2.5\"/></svg>Free shipping $99+</span>" +
      "<span><svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><path d=\"M17 1l4 4-4 4\"/><path d=\"M3 11V9a4 4 0 0 1 4-4h14\"/><path d=\"M7 23l-4-4 4-4\"/><path d=\"M21 13v2a4 4 0 0 1-4 4H3\"/></svg>Easy exchanges</span>" +
      "<span><svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><path d=\"M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z\"/><path d=\"M9 12l2 2 4-4\"/></svg>Money-back guarantee</span>" +
    "</div>";
    inner.appendChild(slot);
  }

  function announce(root, message) {
    var node = root.querySelector("[data-tk9-ctu-announcer]");
    if (node) node.textContent = message;
  }

  // The theme block's compare-at pill renders server-side with a 💰 emoji;
  // normalize its voice without touching the Liquid math. One-shot text edit
  // after each render/morph — no observers.
  function tidyLineSavingsBadges() {
    document.querySelectorAll(".cart-savings-badge").forEach(function (badge) {
      var text = badge.textContent.replace(/💰/g, "").replace(/\s+/g, " ").trim();
      if (badge.textContent !== text) badge.textContent = text;
    });
  }

  function restoreFocus(root) {
    var host = moduleHost(root);
    var active = document.activeElement;
    if (active && active !== document.body && (root.contains(active) || host.contains(active))) return;
    var next = root.querySelector("[data-tk9-ctu-primary-slot] [data-tk9-ctu-add]") ||
      host.querySelector("[data-tk9-ctu-add]") ||
      root.querySelector(".tk9-ctu__title");
    if (next && typeof next.focus === "function") {
      try {
        next.focus({ preventScroll: true });
      } catch (error) {
        next.focus();
      }
    }
  }

  function render(root, config, state) {
    var cart = state.cart || { items: [], item_count: 0, total_price: 0 };
    var panel = root.querySelector("[data-tk9-ctu-panel]");
    // Emptied-cart handoff MUST precede the grid guard: when the theme
    // re-renders an empty cart it deletes the modules host, so a post-guard
    // check never runs and the stale band + sticky bar sit over an empty
    // cart. (After reload the panel is not Liquid-rendered — cannot loop.)
    if (panel && !cart.item_count) {
      window.location.reload();
      return;
    }
    var host = moduleHost(root);
    var grid = host.querySelector("[data-tk9-ctu-offers]");
    if (!panel || !grid) return;

    var group = chooseGroup(state.rules || {}, cart);
    var eligible = resolvedOffers(group, cart, state.prices);
    var model = familyModel(group);
    state.currentGroup = group;
    panel.setAttribute("data-tk9-setup-family", model.key);
    if (host !== root) host.setAttribute("data-tk9-setup-family", model.key);

    var familyNode = root.querySelector("[data-tk9-ctu-family]");
    if (familyNode) familyNode.textContent = model.label;

    // Decision ladder: one featured + two quiet rows. Everything else
    // eligible flows into the discovery carousel — shown, not cut.
    // On mobile, keep the first task simple: cart lines before merchandising,
    // with the sticky checkout still available.
    var cartFirstMobile = window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
    var usedIds = {};
    var usedProductIds = {};
    var track = function (offer) {
      usedIds[offer.id] = true;
      if (offer.productId) usedProductIds[String(offer.productId)] = true;
    };
    var primary = eligible[0] || null;
    if (primary) track(primary);
    // Preview flag (?tk9_dualfeat=1): two featured cards side by side.
    // Owner is evaluating this against the single card; not on by default.
    // Dual featured is the desktop default (owner delegated the call,
    // 2026-07-03). ?tk9_dualfeat=0 is the kill switch. Mobile keeps the
    // single card below the cart lines.
    var dualFeat = !cartFirstMobile && !/[?&]tk9_dualfeat=0/.test(window.location.search || "");
    var secondary = null;
    var compacts = [];
    eligible.slice(1).forEach(function (offer) {
      if (dualFeat && !secondary && !requiresRequiredProperties(offer)) {
        secondary = offer;
        track(offer);
        return;
      }
      if (compacts.length >= 2 || requiresRequiredProperties(offer)) return;
      compacts.push(offer);
      track(offer);
    });
    var carousel = eligible.filter(function (offer) {
      return !usedIds[offer.id] && !requiresRequiredProperties(offer);
    }).slice(0, 8);
    // Personalization offers outside the featured slot become View cards —
    // the product page owns the input flow.
    var needsInput = eligible.filter(function (offer) {
      return !usedIds[offer.id] && requiresRequiredProperties(offer) && productHandle(state, offer.productId);
    }).map(function (offer) {
      return Object.assign({}, offer, { isView: true });
    });
    carousel = carousel.concat(needsInput).concat(linkOffersFor(group, cart, state)).slice(0, 10);
    carousel.forEach(track);
    var cross = crossHeroOffers(state.rules || {}, cart, state, usedProductIds, group);
    // The page carousel is intentionally expansive: the ladder handles the
    // first decision, while the horizontal rail can expose the full remaining
    // family assortment (including the family's ranked cross-product tail).
    // Ten is a safety ceiling, not a four-card merchandising cut.
    var familyTail = carousel.slice(0, 10);
    // Keep the recommendation tree honest: storewide discovery opens only
    // when every eligible offer in the active family has been exhausted.
    // A short family (notably pool) therefore hands off to best sellers after
    // its final add instead of ending the page with no next step.
    var discovery = eligible.length ? [] : cross.slice(0, 4);

    state.currentOffers = eligible.concat(discovery);
    state.moduleCounts = {
      ladder: (primary ? 1 : 0) + (secondary ? 1 : 0) + compacts.length,
      carousel: familyTail.length,
      cross: discovery.length
    };

    // Featured card lives in the band on desktop; mobile moves it below cart
    // lines so the order itself is the first reviewed object.
    var primarySlot = root.querySelector("[data-tk9-ctu-primary-slot]");
    if (primarySlot) {
      if (primary && secondary) {
        primarySlot.innerHTML = "<div class=\"tk9-ctu__dual\">" +
          renderPrimaryOffer(config, state, primary) +
          renderPrimaryOffer(config, state, secondary) +
        "</div>";
        primarySlot.hidden = false;
      } else if (primary && !cartFirstMobile) {
        primarySlot.innerHTML = renderPrimaryOffer(config, state, primary);
        primarySlot.hidden = false;
      } else {
        primarySlot.innerHTML = "";
        primarySlot.hidden = true;
      }
    }
    var dealNote = DEAL_NOTES[group && group.id] || "";
    var heroNoun = HERO_NOUN[group && group.id] || "";
    var mobilePrimaryHtml = primary && cartFirstMobile ? renderPrimaryOffer(config, state, primary) : "";
    if (mobilePrimaryHtml || compacts.length) {
      grid.innerHTML = "<p class=\"tk9-ctu__section-intro\">" + escapeHtml(cartFirstMobile && primary ? "Recommended next" : heroNoun ? "Most added with your " + heroNoun : "Most added together") + "</p>" +
        (dealNote ? "<p class=\"tk9-ctu__deal-note\">" + escapeHtml(dealNote) + "</p>" : "") +
        mobilePrimaryHtml +
        compacts.map(function (offer) {
          return renderCompactOffer(config, state, offer);
        }).join("");
      grid.hidden = false;
    } else if (!primary && !carousel.length && !cross.length) {
      grid.innerHTML = "<div class=\"tk9-ctu__empty\">You're all covered. Checkout is ready when you are.</div>";
      grid.hidden = false;
    } else {
      grid.innerHTML = "";
      grid.hidden = true;
    }

    // Merge-history marker: carousel.concat(cross) previously mixed family
    // and storewide cards. One rail now renders at a time with honest semantics. Remaining offers from the
    // active family are "Add finishing touches". Only after that family tail
    // is exhausted do storewide heroes appear as "Dog parents also shop".
    var familyTailHeadings = {
      "harness-setup": "Useful extras for your harness",
      "seat-cover-setup": "Useful extras for your backseat",
      "pool-setup": "Useful pool-day extras",
      "fan-setup": "More comfort for the ride",
      "walk-accessory-route": "Useful road-trip extras",
      "smart-best-sellers": "More gear for everyday dog life"
    };
    var familyTailHeading = familyTailHeadings[group && group.id] || "Useful extras for your setup";
    renderCarousel(host, "[data-tk9-ctu-more-list]", "[data-tk9-ctu-more]", config, state, familyTail, "carousel", "", familyTailHeading);
    renderCarousel(host, "[data-tk9-ctu-cross-list]", "[data-tk9-ctu-cross]", config, state, discovery, "cross_hero", "", "More gear for everyday dog life");

    renderIncluded(root, config, state.rules, group && group.includedPerkKey, group, cart);
    renderProtection(root, config, state);
    updateTotals(root, state);
    updateThemeCheckoutButton(state);
    tidyLineSavingsBadges();
    decorateCartLines(state);
    decoratePatchLines(state);
    decorateTextlessPatchLines(state);
    renderProofModule(root, state);
    decorateRailTotal(state);
    upgradeTrustRow();
    document.documentElement.classList.remove("tk9-ctu-degraded");
    document.documentElement.classList.add("tk9-ctu-ready");
    document.documentElement.classList.add("tk9-ctu-hydrated-once");
    emitViewOnce(config, state);
  }

  /* ------------------------------------------------------------------ */
  /* Data refresh + theme section morphing                               */
  /* ------------------------------------------------------------------ */

  function refresh(root, config, state) {
    var wants = [
      fetchJson(config.cartUrl),
      state.rules ? Promise.resolve(state.rules) : fetchJson(config.rulesUrl),
      state.prices ? Promise.resolve(state.prices) : fetchJson(config.pricesUrl).catch(function () { return null; })
    ];
    return Promise.all(wants).then(function (results) {
      state.cart = results[0];
      state.rules = results[1];
      state.prices = results[2];
      render(root, config, state);
      return state.cart;
    });
  }

  function degrade(root, config, state, attempt) {
    // Checkout independence: a data failure only removes the hydrated
    // modules. The Liquid band, totals, and CTAs stay on screen.
    var host = moduleHost(root);
    var primarySlot = root.querySelector("[data-tk9-ctu-primary-slot]");
    if (primarySlot) {
      primarySlot.innerHTML = "";
      primarySlot.hidden = true;
    }
    var grid = host.querySelector("[data-tk9-ctu-offers]");
    if (grid) grid.innerHTML = "";
    var included = host.querySelector("[data-tk9-ctu-included]");
    if (included) included.hidden = true;
    var protect = host.querySelector("[data-tk9-ctu-protect]");
    if (protect) protect.hidden = true;
    ["[data-tk9-ctu-more]", "[data-tk9-ctu-cross]"].forEach(function (selector) {
      var wrap = host.querySelector(selector);
      if (wrap) wrap.hidden = true;
    });
    document.documentElement.classList.add("tk9-ctu-degraded");
    emit(config, state, "tk9_cart_top_upsells_degraded", { tk9_degraded_attempt: attempt });
    if (attempt < 2) {
      setTimeout(function () {
        refresh(root, config, state).catch(function () {
          degrade(root, config, state, attempt + 1);
        });
      }, 1500);
    }
  }

  function mainCartSectionEl() {
    return document.querySelector('[id^="shopify-section-"][id$="__cart-section"]');
  }

  function currentCartRoot(fallback) {
    var section = mainCartSectionEl();
    return (section && section.querySelector("[data-tk9-cart-top-upsells]")) ||
      document.querySelector("[data-tk9-cart-top-upsells]") || fallback;
  }

  function syncThemeCartCount(state) {
    var count = Number(state && state.cart && state.cart.item_count) || 0;
    var icon = document.querySelector("cart-icon");
    if (icon && typeof icon.renderCartBubble === "function") {
      icon.renderCartBubble(count, false, false);
      return;
    }
    // Defensive fallback for older header builds without the custom-element
    // method. Keep the visible count and its accessible sentence in sync.
    document.querySelectorAll(".cart-bubble__text-count").forEach(function (node) {
      node.textContent = count < 100 ? String(count) : "";
    });
    document.querySelectorAll(".cart-bubble__text .visually-hidden").forEach(function (node) {
      node.textContent = "Total items in cart: " + count;
    });
  }

  function morphThemeCart(state) {
    var el = mainCartSectionEl();
    if (!el) return Promise.reject(new Error("cart section not found"));
    var sectionId = el.id.replace(/^shopify-section-/, "");
    var url = new URL(window.location.href);
    url.searchParams.set("sections", sectionId);
    return fetch(url.toString(), { credentials: "same-origin" })
      .then(function (response) {
        if (!response.ok) throw new Error("sections fetch failed");
        return response.json();
      })
      .then(function (sections) {
        var html = sections && sections[sectionId];
        if (!html) throw new Error("section html missing");
        var holder = document.createElement("div");
        holder.innerHTML = html;
        var inner = holder.querySelector('[id^="shopify-section-"]');
        el.innerHTML = inner ? inner.innerHTML : html;
        state.internalMutation = true;
        document.dispatchEvent(new CustomEvent("cart:updated"));
        setTimeout(function () { state.internalMutation = false; }, 400);
      });
  }

  function afterCartMutation(root, config, state) {
    // Morph FIRST: the module host lives inside the morphed section, so the
    // server render resets it to skeletons — then refresh() hydrates the
    // fresh host. The reverse order would wipe the just-rendered offers.
    document.documentElement.classList.remove("tk9-ctu-ready");
    return morphThemeCart(state)
      .then(function () {
        var freshRoot = currentCartRoot(root);
        state.root = freshRoot;
        return refresh(freshRoot, config, state).then(function () { return freshRoot; });
      })
      .then(function (freshRoot) {
        updateThemeCheckoutButton(state);
        syncThemeCartCount(state);
        bindStickyBar(freshRoot, state);
        return freshRoot;
      })
      .catch(function () {
        // Morph failure falls back to a full reload — never a stale cart.
        window.location.reload();
      });
  }

  /* ------------------------------------------------------------------ */
  /* Actions                                                             */
  /* ------------------------------------------------------------------ */

  function setBusy(root, busy) {
    document.querySelectorAll("[data-tk9-ctu-add], [data-tk9-ctu-seed-scenario], [data-tk9-ctu-protect-toggle]").forEach(function (button) {
      button.disabled = Boolean(busy);
    });
  }

  function findOffer(state, id) {
    return (state.currentOffers || []).find(function (offer) {
      return String(offer.id) === String(id);
    }) || null;
  }

  /* ------------------------------------------------------------------ */
  /* In-tile variant picker — discovery tiles add to cart without        */
  /* leaving the page. Option structure comes from the product .js       */
  /* endpoint (plus the tk9-card-variants theme asset for products past  */
  /* the 250-variant Liquid/.js cap); every PRINTED price still comes    */
  /* from the verified map only, and adds run through addOffer.          */
  /* ------------------------------------------------------------------ */

  function loadPickerData(config, state, productId, handle) {
    state.pickerData = state.pickerData || {};
    if (state.pickerData[productId]) return Promise.resolve(state.pickerData[productId]);
    return fetchJson("/products/" + handle + ".js").then(function (product) {
      var options = product.options || [];
      var variants = product.variants || [];
      var colorIndex = options.findIndex(function (option) {
        return /color/i.test(option.name || "");
      });
      var chip = {};
      if (colorIndex >= 0) {
        variants.forEach(function (variant) {
          var value = [variant.option1, variant.option2, variant.option3][colorIndex];
          if (value && !chip[value] && variant.featured_image && variant.featured_image.src) {
            chip[value] = variant.featured_image.src;
          }
        });
      }
      var vidImage = {};
      variants.forEach(function (variant) {
        if (variant.featured_image && variant.featured_image.src) vidImage[variant.id] = variant.featured_image.src;
      });
      var data = {
        optionNames: options.map(function (option) { return option.name; }),
        optionValues: options.map(function (option) { return (option.values || []).slice(); }),
        colorIndex: colorIndex,
        chip: chip,
        vidImage: vidImage,
        rows: variants.map(function (variant) {
          return [variant.id, variant.option1 || null, variant.option2 || null, variant.option3 || null];
        })
      };
      if (variants.length < 250) {
        state.pickerData[productId] = data;
        return data;
      }
      return fetchJson(String(config.assetBaseUrl || "") + "tk9-card-variants-" + productId + ".json").then(function (full) {
        if (full && full.v && full.v.length) {
          data.rows = full.v.map(function (row) { return [row[0], row[1], row[2], row[3]]; });
          full.v.forEach(function (row) {
            if (row[7]) data.vidImage[row[0]] = row[7];
          });
          Object.keys(full.chip || {}).forEach(function (value) {
            if (full.chip[value] && !data.chip[value]) data.chip[value] = full.chip[value];
          });
        }
        state.pickerData[productId] = data;
        return data;
      }).catch(function () {
        state.pickerData[productId] = data;
        return data;
      });
    });
  }

  function pickerSelections(panel) {
    var values = [null, null, null];
    Array.prototype.slice.call(panel.querySelectorAll("[data-tk9-ctu-chip].is-selected")).forEach(function (chip) {
      values[Number(chip.getAttribute("data-tk9-opt"))] = chip.getAttribute("data-tk9-value");
    });
    Array.prototype.slice.call(panel.querySelectorAll("select[data-tk9-ctu-picker-select]")).forEach(function (select) {
      values[Number(select.getAttribute("data-tk9-opt"))] = select.value;
    });
    return values;
  }

  function resolvePickerRow(data, selections) {
    for (var i = 0; i < data.rows.length; i += 1) {
      var row = data.rows[i];
      var match = true;
      for (var oi = 0; oi < data.optionNames.length; oi += 1) {
        if (selections[oi] !== null && row[oi + 1] !== selections[oi]) { match = false; break; }
      }
      if (match) return row;
    }
    return null;
  }

  function defaultPickerSelections(state, data) {
    // Preselect the cheapest map-verified combo — the same number the
    // tile's "From $X" anchor shows, so Add is truthful immediately.
    var best = null;
    var bestCents = Infinity;
    data.rows.forEach(function (row) {
      var live = verifiedPrice(state, row[0]);
      if (live && live.cents < bestCents) { bestCents = live.cents; best = row; }
    });
    if (!best) best = data.rows[0];
    return data.optionNames.map(function (name, index) { return best[index + 1]; });
  }

  function syncPicker(state, offer, panel) {
    var card = panel.closest("[data-tk9-ctu-offer]");
    var data = (state.pickerData || {})[offer.productId];
    if (!data) return;
    var selections = pickerSelections(panel);
    var row = resolvePickerRow(data, selections);
    var live = row ? verifiedPrice(state, row[0]) : null;
    // The tile photo tracks the exact resolved variant; color-level image is
    // the fallback while a combo is unresolved.
    var photo = (row && data.vidImage[row[0]]) ||
      (data.colorIndex >= 0 && selections[data.colorIndex] && data.chip[selections[data.colorIndex]]) || "";
    if (card && photo) {
      var media = card.querySelector("img");
      if (media && !media.closest("[data-tk9-ctu-picker]") && media.src !== photo) {
        media.src = photo;
        media.removeAttribute("srcset");
      }
    }
    panel.setAttribute("data-tk9-ctu-picker-vid", row && live ? String(row[0]) : "");
    var button = panel.querySelector("[data-tk9-ctu-picker-add]");
    if (!button) return;
    if (!row) { button.disabled = true; button.textContent = "Unavailable"; }
    else if (!live) { button.disabled = true; button.textContent = "Sold out"; }
    else { button.disabled = false; button.textContent = "Add to cart"; }
    // The tile price row owns the number: swap "From $X" for the exact
    // verified price once a combo resolves.
    if (card && live) {
      var priceNode = card.querySelector(".tk9-velocity-offer__price");
      if (priceNode) {
        var compare = live.compareAtCents > live.cents ? "<s>" + escapeHtml(money(live.compareAtCents)) + "</s>" : "";
        priceNode.innerHTML = "<b>" + escapeHtml(money(live.cents)) + "</b>" + compare;
      }
    }
  }

  function renderPickerPanel(config, state, offer, data, panel) {
    var selections = defaultPickerSelections(state, data);
    var html = "";
    data.optionNames.forEach(function (name, index) {
      var values = data.optionValues[index].filter(function (value) {
        return data.rows.some(function (row) { return row[index + 1] === value; });
      });
      if (!values.length) return;
      if (index === data.colorIndex) {
        html += "<div class=\"tk9-ctu__picker-swatches\" role=\"group\" aria-label=\"" + escapeHtml(name) + "\">" + values.map(function (value) {
          var img = data.chip[value];
          var selected = selections[index] === value ? " is-selected" : "";
          var inner = img
            ? "<img src=\"" + escapeHtml(img) + "\" alt=\"" + escapeHtml(value) + "\" loading=\"lazy\" width=\"32\" height=\"32\">"
            : "<span>" + escapeHtml(value) + "</span>";
          return "<button type=\"button\" class=\"tk9-ctu__chip" + (img ? "" : " tk9-ctu__chip--text") + selected + "\" data-tk9-ctu-chip data-tk9-opt=\"" + index + "\" data-tk9-value=\"" + escapeHtml(value) + "\" title=\"" + escapeHtml(value) + "\" aria-label=\"" + escapeHtml(name + ": " + value) + "\">" + inner + "</button>";
        }).join("") + "</div>";
      } else {
        html += "<label class=\"tk9-ctu__picker-field\"><span>" + escapeHtml(name.replace(/^Choose an? /i, "")) + "</span>" +
          "<select data-tk9-ctu-picker-select data-tk9-opt=\"" + index + "\">" + values.map(function (value) {
            return "<option value=\"" + escapeHtml(value) + "\"" + (selections[index] === value ? " selected" : "") + ">" + escapeHtml(value) + "</option>";
          }).join("") + "</select></label>";
      }
    });
    html += "<button class=\"tk9-ctu__add tk9-ctu__add--tile\" type=\"button\" data-tk9-ctu-picker-add=\"" + escapeHtml(offer.id) + "\" data-tk9-ctu-module=\"picker\">Add to cart</button>";
    panel.innerHTML = html;
    syncPicker(state, offer, panel);
  }

  function togglePicker(root, config, state, button) {
    var card = button.closest("[data-tk9-ctu-offer]");
    var panel = card && card.querySelector("[data-tk9-ctu-picker]");
    var offer = findOffer(state, button.getAttribute("data-tk9-ctu-choose"));
    if (!panel || !offer) return;
    if (!panel.hidden) { panel.hidden = true; return; }
    emit(config, state, "tk9_cart_top_upsell_picker_open", {
      tk9_offer_id: offer.id,
      tk9_product_id: offer.productId || ""
    });
    panel.hidden = false;
    if (panel.getAttribute("data-tk9-ctu-picker-ready")) return;
    panel.innerHTML = "<div class=\"tk9-ctu__picker-loading\">Loading options…</div>";
    loadPickerData(config, state, offer.productId, button.getAttribute("data-tk9-ctu-handle")).then(function (data) {
      if (!data || !data.rows.length || !data.optionNames.length) throw new Error("no options");
      panel.setAttribute("data-tk9-ctu-picker-ready", "1");
      renderPickerPanel(config, state, offer, data, panel);
    }).catch(function () {
      // Never a dead end: fall back to the product page.
      var handle = button.getAttribute("data-tk9-ctu-handle");
      if (handle) window.location.href = "/products/" + handle;
    });
  }

  function selectPickerChip(state, chip) {
    var panel = chip.closest("[data-tk9-ctu-picker]");
    var card = chip.closest("[data-tk9-ctu-offer]");
    var offer = findOffer(state, card && card.getAttribute("data-tk9-ctu-offer"));
    if (!panel || !offer) return;
    Array.prototype.slice.call(panel.querySelectorAll("[data-tk9-ctu-chip][data-tk9-opt=\"" + chip.getAttribute("data-tk9-opt") + "\"]")).forEach(function (other) {
      other.classList.remove("is-selected");
    });
    chip.classList.add("is-selected");
    syncPicker(state, offer, panel);
  }

  function pickerAddToCart(root, config, state, button) {
    var card = button.closest("[data-tk9-ctu-offer]");
    var panel = card && card.querySelector("[data-tk9-ctu-picker]");
    var offer = findOffer(state, button.getAttribute("data-tk9-ctu-picker-add"));
    if (!panel || !offer) return;
    var variantId = Number(panel.getAttribute("data-tk9-ctu-picker-vid") || 0);
    if (!variantId) return;
    var picked = Object.assign({}, offer, {
      variantId: variantId,
      isView: false,
      action: "add",
      upgradePlan: null
    });
    addOffer(root, config, state, picked, card, button);
  }

  function addOffer(root, config, state, offer, card, button) {
    if (!offer || state.busy) return;
    var errorNode = card && card.querySelector("[data-tk9-ctu-error]");
    var propertyResult = collectProperties(card || root, offer);
    if (propertyResult.error) {
      if (errorNode) {
        errorNode.textContent = propertyResult.error;
        errorNode.hidden = false;
      }
      emit(config, state, "tk9_cart_top_upsell_validation_error", { tk9_offer_id: offer.id });
      return;
    }
    if (errorNode) errorNode.hidden = true;

    var promisedBundle = bundlePricing(state, offer);
    state.busy = true;
    setBusy(root, true);
    if (button) button.textContent = "Adding…";
    var offerModule = (button && button.getAttribute("data-tk9-ctu-module")) || "ladder";
    emit(config, state, "tk9_cart_top_upsell_add", {
      tk9_offer_id: offer.id,
      tk9_upsell_group_id: offer.groupId,
      tk9_variant_id: offer.variantId,
      tk9_product_id: offer.productId || "",
      tk9_offer_module: offerModule
    });

    var request;
    if (Array.isArray(offer.cartQuantityProductIds) && offer.cartQuantityProductIds.length) {
      // A quantity-step offer (currently the second Doggles pair) must
      // increment the existing line. add.js with attribution properties
      // creates a second visually identical line because Shopify keys lines
      // by properties; change.js keeps one honest quantity control and one
      // quantity-scaled price stack.
      var quantityProductIds = offer.cartQuantityProductIds.map(Number);
      var quantityLine = ((state.cart && state.cart.items) || []).find(function (line) {
        return quantityProductIds.indexOf(Number(line.product_id)) !== -1;
      });
      request = quantityLine
        ? fetchJson(config.changeUrl, {
            method: "POST",
            body: JSON.stringify({ id: quantityLine.key, quantity: Number(quantityLine.quantity || 0) + 1 })
          })
        : Promise.reject(new Error("quantity step source line missing"));
    } else if (offer.action === "change_variant" && offer.upgradePlan) {
      var plan = offer.upgradePlan;
      // Line keys are NOT durable across cart mutations — Shopify regenerates
      // them after add.js, so the render-time sourceLine.key 400s ("no valid
      // id"). Re-fetch the cart post-add and swap with the CURRENT key
      // (verified live 2026-07-02: stale key left the single fan in the cart
      // alongside the two-pack).
      request = fetchJson(config.addUrl, {
        method: "POST",
        body: JSON.stringify({
          id: plan.targetVariantId,
          quantity: plan.targetQuantity,
          properties: attributionProperties(config, offer, offerModule)
        })
      }).then(function () {
        return fetchJson(config.cartUrl);
      }).then(function (freshCart) {
        var sourceVid = lineVariantId(plan.sourceLine);
        var freshLine = ((freshCart && freshCart.items) || []).find(function (line) {
          return lineVariantId(line) === sourceVid;
        });
        if (!freshLine) return freshCart;
        return fetchJson(config.changeUrl, {
          method: "POST",
          body: JSON.stringify({ id: freshLine.key, quantity: plan.remainder })
        }).catch(function (error) {
          error.tk9Phase = "swap";
          throw error;
        });
      });
    } else {
      var addProperties = Object.assign(attributionProperties(config, offer, offerModule), propertyResult.properties || {});
      if (isPatchOffer(offer) && card) {
        // Drawer parity: the order line carries the mockup lines so emails
        // and fulfillment render the same artwork the shopper previewed.
        var patchValues = patchFieldValues(card);
        addProperties._tk9_patch_mockup_format = "svg_v4_pair_cart_single_preview";
        // Uppercased to match BOTH the preview artwork and the visible cart
        // property — fulfillment must print exactly what was previewed.
        addProperties._tk9_patch_mockup_line_1 = (patchValues.name || "").toUpperCase();
        addProperties._tk9_patch_mockup_line_2 = (patchValues.phone || "").toUpperCase();
      }
      request = fetchJson(config.addUrl, {
        method: "POST",
        body: JSON.stringify({
          id: offer.variantId,
          quantity: 1,
          properties: addProperties
        })
      });
    }

    request.then(function () {
      if (button) button.textContent = "Added ✓";
      return afterCartMutation(root, config, state).then(function (freshRoot) {
        // Self-verify the promise: if the badge claimed a bundle discount and
        // the function did not deliver one in-cart, flag it for the readout.
        if (promisedBundle) {
          var addedLine = cartVariantLine(state.cart, offer.variantId);
          // Any delivery WORSE than the promised bundle price trips the wire —
          // not just a zero discount. A smaller-than-promised function
          // discount is the same broken promise.
          if (addedLine && Number(addedLine.final_price) > Number(promisedBundle.bundleCents)) {
            emit(config, state, "tk9_bundle_discount_missing", {
              tk9_offer_id: offer.id,
              tk9_variant_id: offer.variantId,
              tk9_promised_off_cents: promisedBundle.offCents,
              tk9_promised_price_cents: promisedBundle.bundleCents,
              tk9_actual_final_cents: Number(addedLine.final_price)
            });
          }
        }
        announce(freshRoot, "Added " + (offer.title || "add-on") + ". Total " + money(state.cart.total_price));
        restoreFocus(freshRoot);
        state.busy = false;
        setBusy(root, false);
      });
    }).catch(function (error) {
      emit(config, state, "tk9_cart_top_upsell_add_error", {
        tk9_offer_id: offer.id,
        tk9_variant_id: offer.variantId,
        tk9_error_status: error.status || "",
        tk9_error_phase: error.tk9Phase || "add"
      });
      var message = error.tk9Phase === "swap"
        ? "The upgrade did not fully apply. Review the cart lines below."
        : "That add-on was not added. Please try again.";
      // Resync first: a partial mutation (upgrade added, source not removed)
      // must be reflected before the shopper acts on the error.
      var safeId = String(offer.id).replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
      afterCartMutation(root, config, state).then(function (freshRoot) {
        var freshCard = root.querySelector("[data-tk9-ctu-offer=\"" + safeId + "\"]");
        if (freshRoot) freshCard = freshRoot.querySelector("[data-tk9-ctu-offer=\"" + safeId + "\"]");
        var freshError = freshCard ? freshCard.querySelector("[data-tk9-ctu-error]") : null;
        if (freshError) {
          freshError.textContent = message;
          freshError.hidden = false;
        }
        announce(freshRoot, message);
        restoreFocus(freshRoot);
        state.busy = false;
        setBusy(root, false);
      }).catch(function () {
        // Whatever else failed, never strand the page with busy=true.
        state.busy = false;
        setBusy(root, false);
      });
    });
  }

  function toggleProtection(root, config, state, id, button) {
    if (state.busy) return;
    var item = ((state.rules && state.rules.protection) || []).find(function (candidate) {
      return String(candidate.id) === String(id);
    });
    if (!item) return;
    var line = cartVariantLine(state.cart, item.variantId);
    state.busy = true;
    setBusy(root, true);
    if (button) button.setAttribute("aria-checked", line ? "false" : "true");

    var request = line
      ? fetchJson(config.changeUrl, {
          method: "POST",
          body: JSON.stringify({ id: line.key, quantity: 0 })
        })
      : fetchJson(config.addUrl, {
          method: "POST",
          body: JSON.stringify({
            id: item.variantId,
            quantity: 1,
            properties: attributionProperties(config, Object.assign({ groupId: "protection" }, item), "protect")
          })
        });

    emit(config, state, line ? "tk9_cart_protection_remove" : "tk9_cart_protection_add", {
      tk9_offer_id: item.id,
      tk9_variant_id: item.variantId
    });

    request.then(function () {
      return afterCartMutation(root, config, state).then(function (freshRoot) {
        announce(freshRoot, (line ? "Removed " : "Added ") + item.title + ". Total " + money(state.cart.total_price));
        restoreFocus(freshRoot);
        state.busy = false;
        setBusy(root, false);
      });
    }).catch(function () {
      if (button) button.setAttribute("aria-checked", line ? "true" : "false");
      state.busy = false;
      setBusy(root, false);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Social proof module (owner 2026-07-04): real Loox reviews, matched   */
  /* to the cart's family, baked at build time (no runtime API). Quotes   */
  /* are VERBATIM Loox review text with real reviewer names; totals and   */
  /* averages verified from live PDP/Loox widgets. Refresh Loox counts    */
  /* before deploy when this proof is touched.                            */
  /* ------------------------------------------------------------------ */

  // Preview gate: owner has seen the mock but not approved for live.
  // Flip to true (or visit with ?tk9_proof=1) to render.
  var PROOF_ENABLED = true;

  var PROOF_STORE_TOTAL = "9,628";
  // Quote pool. Verbatim contiguous Loox sentences; the name AND photo always
  // belong to the same reviewer. color/size come from the review's purchased
  // variant (Loox "Item type") and only steer selection -- they never print.
  var PROOF_BY_FAMILY = {
    "harness": {
      total: "2,036", avg: "4.9", label: "harness reviews",
      quotes: [
        { text: "We love the harness! Definitely have more control over my 120 lb baby boy.", name: "Claudia M.", img: "tk9-proof-harness-1.jpg", color: "lavender", size: "medium" },
        { text: "I really like this harness for my 9 month old boxer. It is helping me control her more and has decreased the pulling.", name: "Jessica E.", img: "tk9-proof-harness-2.jpg", color: "brown", size: "medium" }
      ]
    },
    "seat": {
      total: "1,617", avg: "4.9", label: "seat cover reviews",
      quotes: [
        { text: "My Shepherd loves his new car ride. He feels so secure he falls asleep almost immediately. I love it because it keeps my leather car seats clean and free of rips and tears.", name: "Kelly Z.", img: "tk9-proof-seat-1b.jpg", color: "black", size: "extra-large" },
        { text: "It works like a charm! It covers my seats, makes it extremely comfortable for my dog. I love that he's safely in the backseat now instead of riding upfront.", name: "Shauna K.", img: "tk9-proof-seat-2b.jpg", color: "gray", size: "large" },
        { text: "I'm so happy I got the XL! All 3 of my pups fit perfect!", name: "Erica L.", img: "tk9-proof-seat-3.jpg", size: "extra-large" }
      ]
    },
    "pool": {
      total: PROOF_STORE_TOTAL, avg: "4.9", label: "verified reviews",
      quotes: [
        { text: "Almost 90 degrees in Wisconsin!! My dog loves this pool, it is perfect for keeping cool while fetching his ball.", name: "Amy H.", img: "tk9-proof-pool-1.jpg", size: "large" },
        { text: "Very durable. High quality materials, you don't have to worry about your pets claws ripping this thing.", name: "Kyle P.", img: "tk9-proof-pool-2.jpg", size: "large" }
      ]
    },
    "doggles": {
      total: PROOF_STORE_TOTAL, avg: "4.9", label: "verified reviews",
      quotes: [
        { text: "Maximus is now Road Dog Ready.", name: "Dennis A.", img: "tk9-proof-doggles-1.jpg", color: "black" },
        { text: "Tolya has no problem seeing with the Team K9 eye-pro.", name: "Jonathan L.", img: "tk9-proof-doggles-2b.jpg" }
      ]
    },
    "fans": {
      total: PROOF_STORE_TOTAL, avg: "4.9", label: "verified reviews",
      quotes: [
        { text: "The fans are so quiet, I was worried my boy would be afraid of them, but they didn't bother him. I love the three speeds and how easy they are to install.", name: "Heather S.", img: "tk9-proof-fans-1.jpg" },
        { text: "The heat and humidity are at their peak of summer. This fan has been the best thing I ever bought.", name: "Mary M.", img: "tk9-proof-fans-2.jpg" }
      ]
    },
    "splashpad": {
      total: PROOF_STORE_TOTAL, avg: "4.9", label: "verified reviews",
      quotes: [
        { text: "My Goldendoodle had so much fun with her water pad!! She ran right in like she already knew what to expect! It was almost 100 degrees out and she simply laid in it for about a hour. Total relax.", name: "Cheryl B." },
        { text: "This splash pad is the PERFECT solution. She loves it and so did her big sister Vina the boxer.", name: "Crystal L." }
      ]
    },
    "collar": {
      total: "376", avg: "4.9", label: "collar reviews",
      quotes: [
        { text: "Fits my Mal perfectly! Durable, functional, and very attractive. He's comfortable, and I can control him with the handle.", name: "Kelly M.", img: "tk9-proof-collar-1.jpg" },
        { text: "Great product, looks great as well as being functional. The padding on the inside helps with less irritation than previous designs we have tried for our boy.", name: "Brandon Y.", img: "tk9-proof-collar-2.jpg", color: "black", size: "extra-large" }
      ]
    },
    "leash": {
      total: "243", avg: "4.9", label: "leash reviews",
      quotes: [
        { text: "I love it! I don't think I'll ever go back to a regular leash again, it has the right amount of 'give' to it. And the extra handle is great if I need it.", name: "Lorraine D.", img: "tk9-proof-leash-1.jpg", color: "orange" },
        { text: "Was a little skeptical at first on the bungee factor. Wasn't sure I wanted to give 88 lbs of muscle another 3' of pull but it's been above and beyond what I expected.", name: "Monte L.", img: "tk9-proof-leash-2.jpg", color: "black" }
      ]
    }
  };

  var PROOF_BY_PRODUCT_ID = {
    // Furniture cover has its own PDP Loox count but no approved quote/photo
    // for the cart proof module yet, so it renders a count-only proof line.
    6735962407011: { total: "165", avg: "4.8", label: "furniture cover reviews", family: "" },
    7090638356579: { total: "2,036", avg: "4.9", label: "harness reviews", family: "harness" },
    6749766811747: { total: "2,174", avg: "4.9", label: "harness reviews", family: "harness" },
    7513423413347: { total: "1,617", avg: "4.9", label: "seat cover reviews", family: "seat" }
  };

  function proofFamilyKey(group) {
    var id = String(group && group.id || "");
    if (id.indexOf("harness") !== -1 || id.indexOf("walk") !== -1) return "harness";
    if (id.indexOf("seat") !== -1) return "seat";
    if (id.indexOf("pool") !== -1) return "pool";
    if (id.indexOf("doggle") !== -1 || id.indexOf("ride") !== -1) return "doggles";
    if (id.indexOf("fan") !== -1) return "fans";
    return "";
  }

  // Family of a cart line by product/title. Fans MUST test before seat: the
  // fans product title contains "Backseat". Protection and safety-belt lines
  // are proof-neutral so a harness cart does not borrow seat-cover reviews.
  function proofLineFamily(line) {
    var productId = Number(line && line.product_id || 0);
    if ([4359637536867, 4418644500579, 7827615121507].indexOf(productId) !== -1) return "fans";
    if ([6905734070371].indexOf(productId) !== -1) return "doggles";
    if ([7383496261731].indexOf(productId) !== -1) return "splashpad";
    if ([6801131569251, 6920870527075].indexOf(productId) !== -1) return "pool";
    if ([7513423413347, 7586514567267, 6845717872739].indexOf(productId) !== -1) return "seat";
    if ([7090638356579, 6749766811747, 4734560632931, 6840101797987, 6877742399587].indexOf(productId) !== -1) return "harness";
    if ([7682685337699].indexOf(productId) !== -1) return "leash";
    if ([6673398980707].indexOf(productId) !== -1) return "collar";
    if ([7666708381795, 6735962407011, 6762308337763, 7831307681891, 7823183151203, 7892526432355, 7994154942563].indexOf(productId) !== -1) return "";

    var t = normalize([line && line.product_title, line && line.title, line && line.variant_title].join(" "));
    if (t.indexOf("shipping insurance") !== -1 || t.indexOf("lifetime warranty") !== -1 || t.indexOf("warranty") !== -1) return "";
    if (t.indexOf("safety belt") !== -1 || t.indexOf("pet safety belt") !== -1 || t.indexOf("car safety belt") !== -1) return "";
    if (t.indexOf("fan") !== -1) return "fans";
    if (t.indexOf("goggle") !== -1 || t.indexOf("doggle") !== -1) return "doggles";
    if (t.indexOf("splash") !== -1) return "splashpad";
    if (t.indexOf("pool") !== -1) return "pool";
    if (t.indexOf("furniture") !== -1 || t.indexOf("couch") !== -1 || t.indexOf("sofa") !== -1 || t.indexOf("recliner") !== -1 || t.indexOf("throw blanket") !== -1 || t.indexOf("hair removal") !== -1) return "";
    if (t.indexOf("seat cover") !== -1 || t.indexOf("single seat cover") !== -1 || t.indexOf("cargo cover") !== -1 || t.indexOf("backseat") !== -1 || t.indexOf("hammock") !== -1) return "seat";
    if (t.indexOf("harness") !== -1 || t.indexOf("patch") !== -1) return "harness";
    if (t.indexOf("leash") !== -1) return "leash";
    if (t.indexOf("collar") !== -1) return "collar";
    return null;
  }

  var PROOF_COLORS = ["orange", "black", "brown", "green", "pink camo", "multicam", "lavender", "gray", "grey", "beige"];

  function proofTraits(text) {
    var t = String(text || "").toLowerCase();
    var traits = { colors: {}, size: null };
    PROOF_COLORS.forEach(function (color) {
      if (t.indexOf(color) !== -1) traits.colors[color === "grey" ? "gray" : color] = true;
    });
    // Extra-large before large: "Extra-Large" contains the "large" token
    // (same trap as detectSize).
    if (/extra[ -]?large|x-large|\bxl\b/.test(t)) traits.size = "extra-large";
    else if (/\blarge\b/.test(t)) traits.size = "large";
    else if (/\bmedium\b/.test(t)) traits.size = "medium";
    else if (/extra[ -]?small|x-small|\bxs\b/.test(t)) traits.size = "extra-small";
    else if (/\bsmall\b/.test(t)) traits.size = "small";
    return traits;
  }

  function storeProofHead() {
    return { total: PROOF_STORE_TOTAL, avg: "4.9", label: "verified reviews" };
  }

  function productProofHead(line) {
    return PROOF_BY_PRODUCT_ID[Number(line && line.product_id || 0)] || null;
  }

  // Dynamic selection (owner, 2026-07-03): quotes follow the products in the
  // cart, and prefer reviews whose purchased variant matches the shopper's
  // color/size. One family -> its two best quotes under its own review count;
  // mixed cart -> best quote from each of the first two families under the
  // store-wide count. A size-tagged quote never outranks an exact match, so
  // the XL quote only surfaces for XL carts.
  function proofSelection(state, cart) {
    var families = [];
    var traitsByFamily = {};
    var productHeadsByFamily = {};
    var fallbackProductHead = null;
    (cart && cart.items || []).forEach(function (line) {
      var productHead = productProofHead(line);
      if (productHead && !fallbackProductHead) fallbackProductHead = productHead;
      var fam = proofLineFamily(line);
      if (!fam || !PROOF_BY_FAMILY[fam]) return;
      if (productHead && !productHeadsByFamily[fam]) productHeadsByFamily[fam] = productHead;
      if (families.indexOf(fam) === -1) families.push(fam);
      var traits = proofTraits((line.product_title || "") + " " + (line.variant_title || ""));
      var agg = traitsByFamily[fam] || (traitsByFamily[fam] = { colors: {}, sizes: {} });
      Object.keys(traits.colors).forEach(function (color) { agg.colors[color] = true; });
      if (traits.size) agg.sizes[traits.size] = true;
    });
    var lead = proofFamilyKey(state.currentGroup);
    var leadAt = lead ? families.indexOf(lead) : -1;
    if (leadAt > 0) {
      families.splice(leadAt, 1);
      families.unshift(lead);
    }
    if (!families.length && lead && PROOF_BY_FAMILY[lead]) families = [lead];
    if (!families.length) return { head: fallbackProductHead || storeProofHead(), quotes: [] };

    var picks = [];
    var perFamily = families.length > 1 ? 1 : 2;
    families.slice(0, 2).forEach(function (fam) {
      var agg = traitsByFamily[fam] || { colors: {}, sizes: {} };
      var scored = PROOF_BY_FAMILY[fam].quotes.map(function (quote, index) {
        var score = 0;
        if (quote.color && agg.colors[quote.color]) score += 2;
        if (quote.size && agg.sizes[quote.size]) score += 2;
        return { quote: quote, score: score, index: index };
      });
      scored.sort(function (a, b) { return b.score - a.score || a.index - b.index; });
      scored.slice(0, perFamily).forEach(function (entry) { picks.push(entry.quote); });
    });
    var head = families.length === 1
      ? (productHeadsByFamily[families[0]] || PROOF_BY_FAMILY[families[0]])
      : storeProofHead();
    return { head: head, quotes: picks };
  }

  function renderProofModule(root, state) {
    if (!PROOF_ENABLED && !/[?&]tk9_proof=1/.test(window.location.search || "")) return;
    var host = moduleHost(root);
    var modules = host.querySelector(".tk9-ctu__modules") || host;
    var selection = proofSelection(state, state.cart);
    var proof = selection.head;
    var container = modules.querySelector("[data-tk9-ctu-proof]");
    if (!container) {
      container = document.createElement("div");
      container.className = "tk9-ctu__proof";
      container.setAttribute("data-tk9-ctu-proof", "");
      modules.appendChild(container);
    }
    var stars = "<span class=\"tk9-ctu__proof-stars\" aria-hidden=\"true\">\u2605\u2605\u2605\u2605\u2605</span>";
    var assetBase = "";
    var ownScript = document.querySelector("script[src*='tk9-cart-top-upsells']");
    if (ownScript) assetBase = ownScript.src.split("tk9-cart-top-upsells")[0];
    var quoteHtml = selection.quotes.length
      ? "<div class=\"tk9-ctu__proof-quotes\">" +
        selection.quotes.map(function (quote) {
          var photo = quote.img && assetBase
            ? "<img class=\"tk9-ctu__proof-photo\" src=\"" + assetBase + quote.img + "\" alt=\"Customer photo from this review\" loading=\"lazy\" width=\"160\" height=\"160\">"
            : "";
          return "<figure class=\"tk9-ctu__proof-quote" + (photo ? " tk9-ctu__proof-quote--photo" : "") + "\">" + photo +
            "<div class=\"tk9-ctu__proof-quote-body\">" +
              "<blockquote>\u201c" + escapeHtml(quote.text) + "\u201d</blockquote>" +
              "<figcaption>" + escapeHtml(quote.name) + " \u00b7 Verified buyer</figcaption>" +
            "</div>" +
          "</figure>";
        }).join("") +
        "</div>"
      : "";
    container.innerHTML =
      "<div class=\"tk9-ctu__proof-head\">" + stars +
        "<b>" + escapeHtml(proof.avg) + " from " + escapeHtml(proof.total) + " " + escapeHtml(proof.label) + "</b>" +
      "</div>" +
      quoteHtml +
      "<p class=\"tk9-ctu__proof-footer\">Trusted by 200,000+ dog parents. We donate to dog rescues with every order. 60-day guarantee.</p>";
  }

  /* ------------------------------------------------------------------ */
  /* Preview seeding (dev toolbar — reload is fine here)                 */
  /* ------------------------------------------------------------------ */

  function previewSeedsEnabled() {
    try {
      var params = new URLSearchParams(window.location.search || "");
      return params.get("tk9_csr_seed") === "1" || params.get("tk9_csr_preview") === "1";
    } catch (error) {
      return false;
    }
  }

  function seedScenarios() {
    return {
      harness: [{ id: 41613505691747, quantity: 1 }],
      seat: [{ id: 42156188500067, quantity: 1 }],
      pool: [{ id: 39969556299875, quantity: 1 }],
      fan: [{ id: 44107734155363, quantity: 1 }],
      clear: []
    };
  }

  function seedCart(root, config, state, scenarioId) {
    if (!previewSeedsEnabled() || state.busy) return;
    var items = seedScenarios()[scenarioId];
    if (!items) return;
    state.busy = true;
    setBusy(root, true);
    fetchJson(config.clearUrl || "/cart/clear.js", {
      method: "POST",
      body: JSON.stringify({})
    }).then(function () {
      return items.reduce(function (chain, item) {
        return chain.then(function () {
          return fetchJson(config.addUrl, {
            method: "POST",
            body: JSON.stringify({
              id: item.id,
              quantity: item.quantity || 1,
              properties: {
                "_tk9_source": "cart_top_upsells_preview_seed",
                "_tk9_cart_surface_experiment": config.experimentId,
                "_tk9_cart_surface_variant": config.variantId,
                "_tk9_cart_surface": config.surface
              }
            })
          });
        });
      }, Promise.resolve());
    }).then(function () {
      window.location.reload();
    }).catch(function () {
      state.busy = false;
      setBusy(root, false);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sticky bar suppression — fail-open, no MutationObserver.            */
  /* Re-armed after every morph because the observed #checkout node is   */
  /* replaced by section re-renders.                                     */
  /* ------------------------------------------------------------------ */

  function bindStickyBar(root, state) {
    var bar = root.querySelector("[data-tk9-ctu-sticky-bar]");
    if (!bar) return;
    try {
      if (state.stickyObserver) {
        state.stickyObserver.disconnect();
        state.stickyObserver = null;
      }
      // Suppress the bar while the theme rail button is on screen (the band
      // carries no CTA since the money moment was gutted 2026-07-02).
      var targets = [];
      var railButton = document.getElementById("checkout");
      if (railButton) targets.push(railButton);
      if (!targets.length || typeof IntersectionObserver === "undefined") return;
      var visibility = new Map();
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visibility.set(
            entry.target,
            entry.isIntersecting && entry.intersectionRatio >= 0.5 && document.contains(entry.target)
          );
        });
        var suppress = false;
        visibility.forEach(function (isVisible) {
          if (isVisible) suppress = true;
        });
        bar.classList.toggle("is-suppressed", suppress);
        document.body.style.paddingBottom = suppress ? "0px" : "";
      }, { threshold: [0, 0.5, 1] });
      targets.forEach(function (target) {
        observer.observe(target);
      });
      state.stickyObserver = observer;
    } catch (error) {
      // Fail-open: the bar stays visible. Safe stacking beats a missing CTA.
    }
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                              */
  /* ------------------------------------------------------------------ */

  function bind(root, config, state) {
    // Every registration below is document-level, so bind runs ONCE per page
    // no matter how many times this script executes (duplicate section
    // placement, editor section:load re-runs) — stacked copies would
    // double-POST every add through their separate state objects.
    if (window.__tk9CtuDocBound) return;
    window.__tk9CtuDocBound = true;
    // Delegated at document level: the offer/protection buttons live inside
    // the theme cart section, whose nodes are replaced by section morphs.
    document.addEventListener("click", function (event) {
      var seedButton = event.target.closest("[data-tk9-ctu-seed-scenario]");
      if (seedButton) {
        event.preventDefault();
        seedCart(root, config, state, seedButton.getAttribute("data-tk9-ctu-seed-scenario"));
        return;
      }

      var chooseButton = event.target.closest("[data-tk9-ctu-choose]");
      if (chooseButton) {
        event.preventDefault();
        togglePicker(root, config, state, chooseButton);
        return;
      }

      var pickerChip = event.target.closest("[data-tk9-ctu-chip]");
      if (pickerChip) {
        event.preventDefault();
        selectPickerChip(state, pickerChip);
        return;
      }

      var pickerAddButton = event.target.closest("[data-tk9-ctu-picker-add]");
      if (pickerAddButton) {
        event.preventDefault();
        pickerAddToCart(root, config, state, pickerAddButton);
        return;
      }

      var addButton = event.target.closest("[data-tk9-ctu-add]");
      if (addButton) {
        event.preventDefault();
        var card = addButton.closest("[data-tk9-ctu-offer]");
        addOffer(root, config, state, findOffer(state, addButton.getAttribute("data-tk9-ctu-add")), card, addButton);
        return;
      }

      var protectToggle = event.target.closest("[data-tk9-ctu-protect-toggle]");
      if (protectToggle) {
        event.preventDefault();
        toggleProtection(root, config, state, protectToggle.getAttribute("data-tk9-ctu-protect-toggle"), protectToggle);
        return;
      }

      var viewLink = event.target.closest("[data-tk9-ctu-view]");
      if (viewLink) {
        emit(config, state, "tk9_cart_top_upsell_view_click", {
          tk9_offer_id: viewLink.getAttribute("data-tk9-ctu-view")
        });
        return;
      }

      var checkoutButton = event.target.closest("[data-tk9-ctu-checkout]");
      if (checkoutButton) {
        emit(config, state, "tk9_cart_top_checkout_click", { tk9_checkout_affordance: "sticky_bar" });
      }
    });

    document.addEventListener("change", function (event) {
      var select = event.target.closest("select[data-tk9-ctu-picker-select]");
      if (!select) return;
      var panel = select.closest("[data-tk9-ctu-picker]");
      var card = select.closest("[data-tk9-ctu-offer]");
      var offer = findOffer(state, card && card.getAttribute("data-tk9-ctu-offer"));
      if (offer && panel) syncPicker(state, offer, panel);
    });

    // The theme rail button is the only desktop checkout — instrument it so
    // checkout-clicks-per-session reads across both affordances.
    document.addEventListener("click", function (event) {
      if (event.target.closest("#checkout")) {
        emit(config, state, "tk9_cart_top_checkout_click", { tk9_checkout_affordance: "summary_rail" });
      }
    });

    // Textless patch fix-it: save writes the text onto the live line
    // (fresh key from a re-fetch — keys change after any cart mutation).
    document.addEventListener("click", function (event) {
      var button = event.target.closest && event.target.closest("[data-tk9-patchfix-save]");
      if (!button || button.disabled) return;
      var box = button.closest("[data-tk9-patchfix]");
      var input = box && box.querySelector("input");
      var errorNode = box && box.querySelector(".tk9-line-patchfix__error");
      var value = sanitizePatchText(input && input.value).trim().toUpperCase();
      if (!value) {
        if (errorNode) { errorNode.textContent = "Type the name or message first."; errorNode.hidden = false; }
        return;
      }
      button.disabled = true;
      if (errorNode) errorNode.hidden = true;
      var boxKey = box.getAttribute("data-tk9-patchfix-key");
      fetchJson(config.cartUrl).then(function (cart) {
        var target = (cart.items || []).find(function (item) { return String(item.key) === boxKey; }) ||
          (cart.items || []).find(patchTextMissing);
        if (!target) throw new Error("line_gone");
        var props = Object.assign({}, target.properties || {});
        props[PATCH_TEXT_PROP] = value;
        props._tk9_patch_mockup_format = props._tk9_patch_mockup_format || "svg_v4_pair_cart_single_preview";
        props._tk9_patch_mockup_line_1 = value;
        if (!props._tk9_patch_mockup_line_2 && props[PATCH_PHONE_PROP]) props._tk9_patch_mockup_line_2 = String(props[PATCH_PHONE_PROP]);
        return fetchJson("/cart/change.js", {
          method: "POST",
          body: JSON.stringify({ id: target.key, quantity: target.quantity, properties: props })
        });
      }).then(function () {
        emit(config, state, "tk9_patch_text_fixed", { tk9_patch_text_length: value.length });
        return refresh(root, config, state);
      }).catch(function () {
        button.disabled = false;
        if (errorNode) { errorNode.textContent = "Could not save. Try again."; errorNode.hidden = false; }
      });
    });

    // Checkout stays held while any patch line has no printed text. Capture
    // phase so it beats the theme's own handlers; express wallets render in
    // Shopify iframes and cannot be intercepted from here.
    document.addEventListener("click", function (event) {
      if (!state.patchTextMissing) return;
      var trigger = event.target.closest && event.target.closest("button[name='checkout'], #checkout, a[href*='/checkout'], [name='checkout']");
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      var fix = document.querySelector("[data-tk9-patchfix]");
      if (fix) {
        fix.scrollIntoView({ behavior: "smooth", block: "center" });
        fix.classList.remove("tk9-line-patchfix--flash");
        void fix.offsetWidth;
        fix.classList.add("tk9-line-patchfix--flash");
        var fixInput = fix.querySelector("input");
        if (fixInput) fixInput.focus({ preventScroll: true });
        var errorNode = fix.querySelector(".tk9-line-patchfix__error");
        if (errorNode) { errorNode.textContent = "Add your patch text before checkout."; errorNode.hidden = false; }
      }
      emit(config, state, "tk9_patch_text_checkout_blocked", {});
    }, true);

    // Live patch mockup: retype the name/phone and the artwork preview
    // updates in place (drawer parity).
    document.addEventListener("input", function (event) {
      var input = event.target.closest && event.target.closest("[data-tk9-ctu-property], [data-tk9-patchfix] input");
      if (!input) return;
      var clean = sanitizePatchText(input.value);
      if (clean !== input.value) input.value = clean;
      var card = input.closest("[data-tk9-ctu-offer]");
      if (card) updatePatchPreview(card);
    });

    // Enter inside a personalization field adds the offer; it never navigates.
    document.addEventListener("keydown", function (event) {
      if (event.key !== "Enter") return;
      var input = event.target.closest("[data-tk9-ctu-property]");
      if (!input) return;
      event.preventDefault();
      var card = input.closest("[data-tk9-ctu-offer]");
      var addButton = card && card.querySelector("[data-tk9-ctu-add]");
      if (addButton && !addButton.disabled) addButton.click();
    });

    // Theme-side cart edits (quantity steppers, line removes) re-render the
    // theme section without telling us; resync the band, totals, CTAs, and
    // eligibility whenever the cart changes outside this script.
    var syncScheduled = false;
    function scheduleExternalSync() {
      if (state.internalMutation || state.busy || syncScheduled) return;
      syncScheduled = true;
      setTimeout(function () {
        syncScheduled = false;
        var freshRoot = currentCartRoot(root);
        state.root = freshRoot;
        refresh(freshRoot, config, state).then(function () {
          bindStickyBar(freshRoot, state);
        }).catch(function () { /* leave last good render */ });
      }, 250);
    }
    ["cart:updated", "cart:update", "cart:refresh", "cart:change"].forEach(function (name) {
      document.addEventListener(name, scheduleExternalSync);
    });
    document.addEventListener("visibilitychange", function () {
      if (!document.hidden) scheduleExternalSync();
    });

    // The theme's discount box (cart-discount-component) morphs the cart
    // section WITHOUT dispatching any cart event — the shopper applied a code
    // and our offers/decorations vanished into server skeletons (reproduced
    // live 2026-07-02). Observe cart mutations at the network layer instead:
    // one guarded fetch wrap, no observers. Our own mutations are dropped by
    // scheduleExternalSync's busy/internalMutation guards.
    if (!window.__tk9CtuFetchWrapped && typeof window.fetch === "function") {
      window.__tk9CtuFetchWrapped = true;
      var origFetch = window.fetch;
      window.fetch = function (input) {
        var result = origFetch.apply(this, arguments);
        try {
          var url = String((input && input.url) || input || "");
          if (/\/cart\/(add|change|update|clear)(\.js)?([?/]|$)|\/discount\/|[?&]sections=/.test(url)) {
            result.then(function () { scheduleExternalSync(); }, function () {});
          }
        } catch (error) {}
        return result;
      };
    }
  }

  function init() {
    if (initialized) return;
    initialized = true;
    document.querySelectorAll("[data-tk9-cart-top-upsells]").forEach(function (root) {
      var config = parseJsonScript(root, "[data-tk9-ctu-config]");
      var state = {
        cart: null,
        rules: null,
        prices: null,
        busy: false,
        currentOffers: [],
        internalMutation: false,
        stickyObserver: null
      };
      var seedPanel = root.querySelector("[data-tk9-ctu-seed]");
      if (seedPanel && previewSeedsEnabled()) seedPanel.hidden = false;
      bind(root, config, state);
      bindStickyBar(root, state);
      refresh(root, config, state).then(function () {
        announce(root, "");
      }).catch(function () {
        degrade(root, config, state, 1);
      });
      // The rail trust pill mounts with the wallet components — retry after.
      setTimeout(upgradeTrustRow, 3000);
      setTimeout(upgradeTrustRow, 7000);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
